import React, { useState, useEffect, useMemo, useRef, useCallback, lazy, Suspense } from 'react';
import { Helmet } from 'react-helmet';
import { motion, AnimatePresence } from 'framer-motion';
import { useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/components/ui/use-toast';
import { Mic, Music, Users, Star, Clock, MessageSquare, QrCode, Heart, Calendar, MapPin, ExternalLink, ChevronDown, ChevronUp, Info, Send, X, Facebook, Instagram, Twitter, User, ArrowLeft, Loader2 } from 'lucide-react';
// import { supabase } from '@/lib/customSupabaseClient';
import QRCode from 'qrcode.react';
import { config } from '@/lib/config';
import { db } from '@/lib/firebase';
import { doc, getDoc, collection, query, where, onSnapshot, addDoc, getDocs, orderBy, updateDoc, increment, serverTimestamp } from 'firebase/firestore';
import SessionManager from '@/lib/sessionManager';
import { capitalizeWords } from '@/lib/utils';
import { trackPageView, trackSongInteraction } from '@/lib/viewerTracking';
import { isPaidSongRequestForLedger } from '@/lib/payoutLedger';
import { debugLog } from '@/components/DebugConsole';
import A2BPaymentForm from '@/components/a2b/A2BPaymentForm';

const PayPalUSProcessor = lazy(() => import('@/components/paypal/PayPalUSProcessor.jsx'));
import { formatCurrency, DEFAULT_CURRENCY } from '@/lib/currency';
import { isPayPalCheckoutCountry, isPayPalPaymentProvider } from '@/lib/paypalCheckout';

/**
 * Look up the karaoke request the payment backend created for this capture.
 * Retries briefly because the client can return from checkout before the
 * gateway callback has finished finalising.
 */
const findServerCreatedKaraokeRequest = async (transactionReference, attempts = 5) => {
  if (!transactionReference) return null;

  for (let attempt = 0; attempt < attempts; attempt += 1) {
    try {
      const snap = await getDocs(
        query(
          collection(db, 'karaoke_requests'),
          where('transaction_reference', '==', transactionReference)
        )
      );
      if (!snap.empty) {
        const found = snap.docs[0];
        return { id: found.id, firestoreId: found.id, ...found.data() };
      }
    } catch (error) {
      console.warn('Could not look up paid karaoke request:', error?.message || error);
      return null;
    }
    await new Promise(resolve => setTimeout(resolve, 1000));
  }

  return null;
};

const KaraokePublicPage = () => {
  const { urlSlug } = useParams();
  const { toast } = useToast();
  const sessionManager = useMemo(() => new SessionManager(), []);
  const [artist, setArtist] = useState(null);
  
  // ✅ Get artist's currency (fallback to GBP for existing UK karaoke hosts)
  const artistCurrency = useMemo(() => {
    return artist?.currency || DEFAULT_CURRENCY;
  }, [artist?.currency]);

  const usePayPal = isPayPalCheckoutCountry(artist?.country);

  // ✅ Create currency formatter using artist's currency
  const formatArtistCurrency = useCallback((amount) => {
    return formatCurrency(amount, artistCurrency);
  }, [artistCurrency]);
  
  const format = formatArtistCurrency;
  
  // CRITICAL: Android Chrome fix - Force cache clear and service worker unregister on load
  useEffect(() => {
    const isAndroidChrome = /Android/.test(navigator.userAgent) && /Chrome/.test(navigator.userAgent);
    const searchParams = new URLSearchParams(window.location.search);
    const freshParam = searchParams.get('_fresh');
    const cacheBust = searchParams.get('_cb');
    
    // Always clear caches on Android Chrome, or if QR code parameters are present
    if (isAndroidChrome || freshParam === '1' || cacheBust) {
      console.log('🔄 Android Chrome or QR scan detected - forcing complete cache clear');
      
      // IMMEDIATELY unregister all service workers (critical for Android Chrome)
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.getRegistrations().then(registrations => {
          registrations.forEach(registration => {
            registration.unregister().then(success => {
              console.log('🗑️ Unregistered service worker:', registration.scope, success);
            });
          });
        });
      }
      
      // Clear all caches
      if ('caches' in window) {
        caches.keys().then(cacheNames => {
          cacheNames.forEach(cacheName => {
            caches.delete(cacheName);
            console.log('🗑️ Cleared cache:', cacheName);
          });
        });
      }
      
      // Clear localStorage items that might cause issues
      const keysToRemove = ['ysr_cache_clear_version', 'ysr_last_reload', 'ysr_reload_count', '__YSR_SW_REGISTERED__', '__YSR_RELOAD_LISTENER_ADDED__'];
      keysToRemove.forEach(key => {
        localStorage.removeItem(key);
        sessionStorage.removeItem(key);
      });
      
      // Remove cache-busting params from URL after processing
      const newUrl = new URL(window.location);
      newUrl.searchParams.delete('_fresh');
      newUrl.searchParams.delete('_cb');
      newUrl.searchParams.delete('v');
      window.history.replaceState({}, '', newUrl);
      
      console.log('✅ Complete cache clear done - page should load fresh');
    }
  }, []);
  const [liveStatus, setLiveStatus] = useState(null);
  const [requests, setRequests] = useState([]);
  /** Public queue card: expand to show full list beyond first 3 items */
  const [showQueueExpanded, setShowQueueExpanded] = useState(false);
  useEffect(() => {
    if (requests.length <= 3) setShowQueueExpanded(false);
  }, [requests.length]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [myRequests, setMyRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [profileLookupAttempted, setProfileLookupAttempted] = useState(false);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const handleOpenRequestModal = () => {
    setShowRequestModal(true);
  };

  const resolveLiveSessionId = useCallback(async () => {
    if (!artist?.uid) return null;
    if (liveStatus?.session_id) return liveStatus.session_id;
    try {
      const ls = await getDoc(doc(db, 'live_status', artist.uid));
      if (!ls.exists()) return null;
      const d = ls.data();
      if (d.is_live && d.session_id) return d.session_id;
    } catch (_) {
      /* non-blocking */
    }
    return null;
  }, [artist?.uid, liveStatus?.session_id]);

  const skipPaymentModalCartTrackedRef = useRef(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  /** Resolved when skip-queue payment modal opens (webhooks / metadata need correct session_id). */
  const [paymentModalSessionId, setPaymentModalSessionId] = useState(null);

  useEffect(() => {
    if (!showPaymentModal) {
      skipPaymentModalCartTrackedRef.current = false;
      setPaymentModalSessionId(null);
      return;
    }
    if (!artist?.uid || skipPaymentModalCartTrackedRef.current) return;
    let cancelled = false;
    (async () => {
      const sid = await resolveLiveSessionId();
      if (cancelled) return;
      setPaymentModalSessionId(sid || null);
      if (!sid) return;
      trackSongInteraction(artist.uid, 'manual_entry', 'added_to_cart', sid);
      skipPaymentModalCartTrackedRef.current = true;
    })();
    return () => {
      cancelled = true;
    };
  }, [showPaymentModal, artist?.uid, resolveLiveSessionId]);

  const [requestForm, setRequestForm] = useState({
    song_title: '',
    artist_name: '',
    requester_name: '',
    note: '',
    amount_paid: 0,
    skip_queue: false
  });

  // Paid Request Settings
  const [paidRequestSettings, setPaidRequestSettings] = useState({
    enabled: false,
    skipQueuePrice: 5.00,
    maxPaidRequests: 3,
    showWaitTimes: false,
    paidRequestsOnly: false
  });

  // Dynamic Pricing Settings
  const [dynamicPricing, setDynamicPricing] = useState({
    enabled: false,
    groups: []
  });

  // ✅ Christmas Theme State - Check TV Display settings from localStorage
  const [isChristmasTheme, setIsChristmasTheme] = useState(false);
  
  // Check for Christmas theme from TV Display settings (check periodically)
  useEffect(() => {
    const checkChristmasTheme = () => {
      try {
        const tvSettings = localStorage.getItem('tv-display-settings');
        if (tvSettings) {
          const parsed = JSON.parse(tvSettings);
          setIsChristmasTheme(parsed.preset === 'christmas');
        }
      } catch (error) {
        console.warn('Could not read TV Display settings for theme:', error);
      }
    };
    
    checkChristmasTheme();
    // Check every 2 seconds to sync with TV Display changes
    const interval = setInterval(checkChristmasTheme, 2000);
    // Also listen to storage events
    window.addEventListener('storage', checkChristmasTheme);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('storage', checkChristmasTheme);
    };
  }, []);
  const [showSkipQueueModal, setShowSkipQueueModal] = useState(false);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [pendingPaymentData, setPendingPaymentData] = useState(null); // Store payment data if form fields aren't filled
  
  // Get current price (with dynamic pricing support)
  const getCurrentPrice = () => {
    if (!paidRequestSettings?.enabled || !paidRequestSettings?.skipQueuePrice) {
      return paidRequestSettings?.skipQueuePrice || 0;
    }

    // If dynamic pricing is enabled, calculate price based on queue position
    if (dynamicPricing?.enabled && dynamicPricing?.groups && dynamicPricing.groups.length > 0) {
      const queueLength = requests.length;
      // Find matching price group based on queue position (queueLength + 1 would be the position of new request)
      const newRequestPosition = queueLength + 1;
      
      for (const group of dynamicPricing.groups) {
        if (group.from && group.to && group.price && 
            newRequestPosition >= group.from && newRequestPosition <= group.to) {
          console.log(`💰 Dynamic pricing: Position ${newRequestPosition} matches group ${group.from}-${group.to}, price: ${group.price}`);
          return group.price;
        }
      }
      // No match found, fall back to default skip queue price
      console.log(`💰 Dynamic pricing: No match for position ${newRequestPosition}, using default price`);
    }

    // Default: use skip queue price
    return paidRequestSettings.skipQueuePrice;
  };
  
  const currentPrice = getCurrentPrice();
  
  // Wait time calculation for Skip Queue button
  const WAIT_TABLE = {
    3: 11,
    4: 15,
    5: 20,
    6: 24,
    7: 29,
    8: 34,
    9: 38,
    10: 43,
    11: 47,
    12: 52,
    13: 56,
    14: 60,
    15: 65,
  };

  const formatWait = (minutes) => {
    if (minutes < 60) return `${minutes} mins`;
    const hrs = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins ? `${hrs}h ${mins}m` : `${hrs} hr`;
  };

  const getEstimatedWait = (length) => {
    if (length < 3) return null;
    if (WAIT_TABLE[length]) return WAIT_TABLE[length];
    // For 15+ songs, continue the pattern (approximately +4.5 min per song)
    const extra = length - 15;
    const incremental = 65 + extra * 4.5;
    return Math.round(incremental);
  };

  // Calculate queue length and visibility flags
  const queueLength = requests.length;
  const showSkipQueueCTA = 
    paidRequestSettings?.enabled && 
    paidRequestSettings?.skipQueuePrice > 0 && 
    queueLength >= 3;
  
  const estimatedWaitMinutes = getEstimatedWait(queueLength);
  const estimatedWaitLabel = estimatedWaitMinutes ? formatWait(estimatedWaitMinutes) : null;

  // State for alternating button text (900ms interval)
  const [showStarLabel, setShowStarLabel] = useState(true);

  useEffect(() => {
    if (!showSkipQueueCTA) return;
    const interval = setInterval(() => {
      setShowStarLabel((prev) => !prev);
    }, 1200);
    return () => clearInterval(interval);
  }, [showSkipQueueCTA]);
  
  // Custom form fields from DJ
  const [customFormFields, setCustomFormFields] = useState(null);
  const [useCustomForm, setUseCustomForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState({ liveStatus: 'disconnected', requests: 'disconnected' });
  const [activeSongTags, setActiveSongTags] = useState([]);
  const [hasTrackedViewer, setHasTrackedViewer] = useState(false);
  const previousLiveStatusRef = useRef(null);
  const [globalUpNextAnnouncement, setGlobalUpNextAnnouncement] = useState(null);
  /** When set, mobile "Up Next" overlay is hidden for this announcement instance (dismiss or 10s timeout). */
  const [mobileAnnouncementHiddenKey, setMobileAnnouncementHiddenKey] = useState(null);

  const getKaraokeAnnouncementInstanceKey = useCallback((a) => {
    if (!a) return '';
    if (a.announced_at) return String(a.announced_at);
    return [a.song_title, a.artist_name, a.requester_name || ''].join('|');
  }, []);

  // Fetch artist profile
  useEffect(() => {
    let formFieldsUnsubscribe = null;
    
    const fetchArtist = async () => {
      try {
        setProfileLookupAttempted(true);
        
        // Normalize the URL slug: remove trailing slash, convert to lowercase, trim whitespace
        const normalizedSlug = urlSlug?.replace(/\/$/, '').toLowerCase().trim();
        console.log('🔍 Fetching artist with slug:', urlSlug, 'normalized:', normalizedSlug);
        
        if (!normalizedSlug) {
          console.error('❌ No URL slug provided');
          setLoading(false);
          return;
        }
        
        // Query Firestore for profile with matching url_slug (case-insensitive)
        const profilesRef = collection(db, 'profiles');
        
        // Fast case-insensitive lookup: try lowercase first (most common), then original case
        let querySnapshot = null;
        
        // First try normalized lowercase (most common case for new profiles)
        const lowercaseQ = query(profilesRef, where('url_slug', '==', normalizedSlug));
        querySnapshot = await getDocs(lowercaseQ);
        
        // If not found, try with original case (for backward compatibility)
        if (querySnapshot.empty) {
            const originalSlug = urlSlug?.replace(/\/$/, '').trim();
            console.log('🔍 Trying original case slug:', originalSlug);
            const originalQ = query(profilesRef, where('url_slug', '==', originalSlug));
            querySnapshot = await getDocs(originalQ);
        }
        
        // If still not found, try case-insensitive search by getting all profiles and filtering
        if (querySnapshot.empty) {
            console.log('🔍 Trying case-insensitive search across all profiles');
            const allProfilesSnapshot = await getDocs(profilesRef);
            const matchingProfile = allProfilesSnapshot.docs.find(doc => {
                const slug = doc.data().url_slug;
                return slug && slug.toLowerCase() === normalizedSlug;
            });
            if (matchingProfile) {
                console.log('✅ Found profile via case-insensitive search');
                querySnapshot = { docs: [matchingProfile], empty: false };
            }
        }

        if (querySnapshot.empty) {
          console.error('❌ Error fetching artist: Profile not found for slug:', normalizedSlug);
          setLoading(false);
          setArtist(null); // Explicitly set to null so "Not Found" shows
          return;
        }

        const artistData = { 
          id: querySnapshot.docs[0].id, 
          uid: querySnapshot.docs[0].id, // The document ID is the user UID
          ...querySnapshot.docs[0].data() 
        };
        console.log('✅ Artist fetched successfully:', artistData);
        setArtist(artistData);
        
        
        // Load custom form fields for this artist with real-time listener
        formFieldsUnsubscribe = await loadCustomFormFields(artistData.uid);
        
        // Restore session data after artist is loaded
        restoreSessionData();
        
        setLoading(false);
      } catch (error) {
        console.error('❌ Exception fetching artist:', error);
        setLoading(false);
        setArtist(null); // Set to null on error so "Not Found" shows
      }
    };

    if (urlSlug) {
      fetchArtist();
    }
    
    // Cleanup function
    return () => {
      if (formFieldsUnsubscribe) {
        formFieldsUnsubscribe();
      }
    };
  }, [urlSlug]);

  // Track page view once artist is loaded
  useEffect(() => {
    if (artist?.uid && artist?.url_slug) {
      trackPageView(artist.uid, artist.url_slug);
    }
  }, [artist?.uid, artist?.url_slug]);

  // Load custom form fields with real-time listener
  const loadCustomFormFields = async (artistId) => {
    try {
      console.log('📂 Loading custom form fields for artist:', artistId);
      
      // Set up real-time listener for form fields
      const docRef = doc(db, 'karaoke_form_fields', artistId);
      const unsubscribe = onSnapshot(docRef, (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          console.log('📂 Loaded custom form fields:', data);
          
          if (data.fields && Array.isArray(data.fields) && data.fields.length > 0) {
            setCustomFormFields(data.fields);
            setUseCustomForm(true);
            
            // Initialize request form with custom fields
            const customForm = {};
            data.fields.forEach(field => {
              if (field.type === 'checkbox') {
                customForm[field.id] = false;
              } else {
                customForm[field.id] = '';
              }
            });
            setRequestForm(prev => ({ ...prev, ...customForm }));
          }
        } else {
          console.log('📂 No custom form fields found, using default form');
          setUseCustomForm(false);
        }
      }, (error) => {
        console.error('❌ Error listening to custom form fields:', error);
        setUseCustomForm(false);
      });
      
      // Store unsubscribe function for cleanup
      return unsubscribe;
    } catch (error) {
      console.error('❌ Error loading custom form fields:', error);
      setUseCustomForm(false);
    }
  };

  // Restore session data on page load
  const restoreSessionData = () => {
    try {
      console.log('🔄 Restoring session data...');
      
      // Check if we have a valid DJ session
      const { djSessionId, karaokeHostId } = sessionManager.getDjSession();
      
      if (djSessionId && karaokeHostId && karaokeHostId === artist?.uid) {
        console.log('✅ Valid DJ session found, restoring session data');
        
        // Set DJ session context
        sessionManager.setDjSession(djSessionId, artist.uid);
        
        // Get stored requests
        const storedRequests = sessionManager.getRequests();
        console.log('📂 Stored requests found:', Object.keys(storedRequests).length);
        
        if (Object.keys(storedRequests).length > 0) {
          // Convert stored requests to myRequests format
          const restoredRequests = Object.values(storedRequests).map(request => ({
            id: request.firestoreId || Object.keys(storedRequests).find(key => storedRequests[key] === request),
            song_title: request.song_title,
            artist_name: request.artist_name,
            requester_name: request.requester_name,
            note: request.note,
            status: request.status || 'pending',
            queuePosition: request.queuePosition,
            created_at: request.created_at,
            lastUpdated: request.lastUpdated
          }));
          
          setMyRequests(restoredRequests);
          console.log('✅ Session data restored:', restoredRequests.length, 'requests');
        }
      } else {
        console.log('ℹ️ No valid DJ session found, starting fresh');
        sessionManager.clearCurrentSession();
      }
    } catch (error) {
      console.error('❌ Error restoring session data:', error);
      sessionManager.clearCurrentSession();
    }
  };



  // Fetch live status and requests
  useEffect(() => {
    if (!artist) return;

    const fetchLiveStatus = async () => {
      try {
        console.log('🔍 KaraokePublicPage: Fetching live status for artist:', artist.uid);
        const liveStatusDoc = await getDoc(doc(db, 'live_status', artist.uid));

        if (liveStatusDoc.exists()) {
          const statusData = liveStatusDoc.data();
          console.log('✅ KaraokePublicPage: Live status fetched:', statusData);
          setLiveStatus(statusData);
          
          // Set DJ session context for session manager
          if (statusData.session_id) {
            sessionManager.setDjSession(statusData.session_id, artist.uid);
            console.log('🎤 Session Manager: Set DJ session context:', statusData.session_id, artist.uid);
          }
          
          // Update active song tags for filtering
          if (statusData?.active_song_tags) {
            setActiveSongTags(statusData.active_song_tags);
          }

          // Load paid request settings with defaults
          if (statusData?.paid_request_settings) {
            console.log('💰 Loaded paid request settings:', statusData.paid_request_settings);
            const settings = statusData.paid_request_settings;
            setPaidRequestSettings({
              enabled: settings.enabled || false,
              skipQueuePrice: settings.skipQueuePrice || 5.00,
              maxPaidRequests: settings.maxPaidRequests || 3,
              showWaitTimes: settings.showWaitTimes !== undefined ? settings.showWaitTimes : (settings.enabled || false),
              paidRequestsOnly: settings.paidRequestsOnly || false
            });
          } else {
            console.log('ℹ️ No paid request settings found in live_status');
          }
        } else {
          console.log('ℹ️ KaraokePublicPage: No live status document found');
          setLiveStatus(null);
          
          // Clear session when DJ goes offline
          sessionManager.clearCurrentSession();
        }
      } catch (error) {
        console.error('❌ KaraokePublicPage: Error fetching live status:', error);
      }
    };

    const fetchRequests = async () => {
      try {
        console.log('🔄 Fetching requests for artist:', artist.uid);
        
        const requestsRef = collection(db, 'karaoke_requests');
        
        // Fetch approved requests
        const approvedQuery = query(
          requestsRef,
          where('karaoke_host_id', '==', artist.uid),
          where('status', '==', 'approved')
        );
        
        const approvedSnapshot = await getDocs(approvedQuery);
        const approvedData = approvedSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        // Fetch pending requests
        const pendingQuery = query(
          requestsRef,
          where('karaoke_host_id', '==', artist.uid),
          where('status', '==', 'pending')
        );
        
        const pendingSnapshot = await getDocs(pendingQuery);
        const pendingData = pendingSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        // Sort by paid requests first, then by approved_at timestamp
        const sortedApprovedData = approvedData.sort((a, b) => {
          // First, prioritize paid requests (skip_queue = true)
          if (a.skip_queue && !b.skip_queue) return -1;
          if (!a.skip_queue && b.skip_queue) return 1;
          
          // If both have the same paid status, sort by approval time
          const aTime = a.approved_at ? new Date(a.approved_at).getTime() : 0;
          const bTime = b.approved_at ? new Date(b.approved_at).getTime() : 0;
          return aTime - bTime; // Oldest first (first approved = first in queue)
        });

        console.log('✅ Approved requests fetched:', sortedApprovedData.length);
        console.log('✅ Pending requests fetched:', pendingData.length);
        setRequests(sortedApprovedData || []);
        setPendingRequests(pendingData || []);
      } catch (error) {
        console.error('❌ Exception fetching requests:', error);
      }
    };

    fetchLiveStatus();
    fetchRequests();
  }, [artist]);

  // Set up real-time listeners
  useEffect(() => {
    if (!artist) return;

    console.log('🔧 KaraokePublicPage: Setting up Firestore listeners for artist:', artist.uid);

    let isFirstLoad = true;

    // Listen for live status changes
    const liveStatusUnsubscribe = onSnapshot(
      doc(db, 'live_status', artist.uid),
      async (doc) => {
        if (doc.exists()) {
          const data = doc.data();
          console.log('🔄 KaraokePublicPage: Live status changed:', data);
          
          const wasLive = previousLiveStatusRef.current?.is_live;
          const nowLive = data.is_live === true;
          
          setLiveStatus(data);
          
          // Track viewer: increment only once when karaoke is live and page first loads, or when karaoke goes from offline to online
          if (nowLive && (isFirstLoad || (!wasLive && nowLive))) {
            try {
              const liveStatusRef = doc(db, 'live_status', artist.uid);
              await updateDoc(liveStatusRef, {
                viewer_count: increment(1),
                last_viewer_update: serverTimestamp()
              });
              console.log('✅ Viewer count incremented for karaoke:', artist.uid);
              setHasTrackedViewer(true);
            } catch (error) {
              console.error('❌ Error incrementing viewer count:', error);
            }
          }
          
          previousLiveStatusRef.current = data;
          isFirstLoad = false;
          
          if (data?.active_song_tags) {
            setActiveSongTags(data.active_song_tags);
          }

          // Update paid request settings
          if (data?.paid_request_settings) {
            console.log('💰 Updated paid request settings:', data.paid_request_settings);
            setPaidRequestSettings(data.paid_request_settings);
          }

          // Update dynamic pricing settings
          if (data?.dynamic_pricing) {
            console.log('💰 Updated dynamic pricing settings:', data.dynamic_pricing);
            setDynamicPricing(data.dynamic_pricing);
          } else {
            setDynamicPricing({ enabled: false, groups: [] });
          }

          // Store up_next_announcement for later checking (will check in useEffect when myRequests updates)
          if (data?.up_next_announcement) {
            console.log('📢 Up Next announcement received:', data.up_next_announcement);
          }
          
          // If karaoke goes offline, reset viewer tracking
          if (data.is_live === false) {
            setHasTrackedViewer(false);
          }
        } else {
          console.log('ℹ️ KaraokePublicPage: No live status document found - karaoke is offline');
          setLiveStatus(null);
          previousLiveStatusRef.current = { is_live: false };
          isFirstLoad = false;
          setHasTrackedViewer(false);
          // Clear session data when karaoke goes offline
          sessionManager.clearCurrentSession();
          setMyRequests([]);
        }
      },
      (error) => {
        console.error('❌ KaraokePublicPage: Error listening to live status:', error);
      }
    );

    // Listen for approved requests changes
    const approvedRequestsUnsubscribe = onSnapshot(
      query(
        collection(db, 'karaoke_requests'),
        where('karaoke_host_id', '==', artist.uid),
        where('status', '==', 'approved')
      ),
      (snapshot) => {
        const requestsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        // Sort by paid requests first, then by approved_at timestamp
        const sortedRequests = requestsData.sort((a, b) => {
          // First, prioritize paid requests (skip_queue = true)
          if (a.skip_queue && !b.skip_queue) return -1;
          if (!a.skip_queue && b.skip_queue) return 1;
          
          // If both have the same paid status, sort by approval time
          const aTime = a.approved_at ? new Date(a.approved_at).getTime() : 0;
          const bTime = b.approved_at ? new Date(b.approved_at).getTime() : 0;
          return aTime - bTime; // Oldest first (first approved = first in queue)
        });
        
        console.log('🎵 KaraokePublicPage: Approved requests changed:', sortedRequests.length);
        setRequests(sortedRequests);
        
        // Update session manager with real-time queue updates
        sortedRequests.forEach((request, index) => {
          if (sessionManager.hasRequest(request.id)) {
            sessionManager.updateRequest(request.id, {
              status: 'approved',
              queuePosition: index + 1,
              lastUpdated: new Date().toISOString()
            });
            console.log('🎤 Session Manager: Updated request status to approved:', request.id, 'Position:', index + 1);
          }
        });
        
        // Update my requests to show approved status
        const myApprovedRequests = filterMyRequests(sortedRequests);
        setMyRequests(prev => {
          const updated = [...prev];
          myApprovedRequests.forEach(approved => {
            const index = updated.findIndex(req => req.id === approved.id);
            if (index !== -1) {
              updated[index] = { ...updated[index], status: 'approved' };
            }
          });
          return updated;
        });
      },
      (error) => {
        console.error('❌ KaraokePublicPage: Error listening to approved requests:', error);
      }
    );

    // Listen for pending requests changes
    const pendingRequestsUnsubscribe = onSnapshot(
      query(
        collection(db, 'karaoke_requests'),
        where('karaoke_host_id', '==', artist.uid),
        where('status', '==', 'pending')
      ),
      (snapshot) => {
        const requestsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        console.log('🎵 KaraokePublicPage: Pending requests changed:', requestsData.length, requestsData);
        setPendingRequests(requestsData);
        
        // Update session manager with pending status
        requestsData.forEach(request => {
          if (sessionManager.hasRequest(request.id)) {
            sessionManager.updateRequest(request.id, {
              status: 'pending',
              lastUpdated: new Date().toISOString()
            });
            console.log('🎤 Session Manager: Updated request status to pending:', request.id);
          }
        });
        
        // Update my requests with pending status - ensure immediate UI update
        const myPendingRequests = filterMyRequests(requestsData);
        console.log('🎤 My pending requests:', myPendingRequests);
        
        setMyRequests(prev => {
          const updated = [...prev];
          myPendingRequests.forEach(pending => {
            const index = updated.findIndex(req => req.id === pending.id);
            if (index !== -1) {
              // Update existing request
              updated[index] = { ...updated[index], ...pending, status: 'pending' };
            } else {
              // Add new request
              updated.push({ ...pending, status: 'pending' });
            }
          });
          console.log('🎤 Updated myRequests:', updated);
          return updated;
        });
      },
      (error) => {
        console.error('❌ KaraokePublicPage: Error listening to pending requests:', error);
      }
    );

    // Listen for denied requests to remove from session
    const deniedRequestsUnsubscribe = onSnapshot(
      query(
        collection(db, 'karaoke_requests'),
        where('karaoke_host_id', '==', artist.uid),
        where('status', '==', 'denied')
      ),
      (snapshot) => {
        const deniedRequests = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        // Remove denied requests from session
        deniedRequests.forEach(request => {
          if (sessionManager.hasRequest(request.id)) {
            sessionManager.removeRequest(request.id);
            setMyRequests(prev => prev.filter(req => req.id !== request.id));
          }
        });
      },
      (error) => {
        console.error('❌ KaraokePublicPage: Error listening to denied requests:', error);
      }
    );

    // Listen for performed requests to remove from session (when marked as "Done")
    const performedRequestsUnsubscribe = onSnapshot(
      query(
        collection(db, 'karaoke_requests'),
        where('karaoke_host_id', '==', artist.uid),
        where('status', '==', 'performed')
      ),
      (snapshot) => {
        const performedRequests = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        console.log('🎵 KaraokePublicPage: Performed requests detected:', performedRequests.length);
        
        // Remove performed requests from session and my requests
        performedRequests.forEach(request => {
          if (sessionManager.hasRequest(request.id)) {
            sessionManager.removeRequest(request.id);
            console.log('🎤 Session Manager: Removed performed request:', request.id);
          }
          setMyRequests(prev => {
            const filtered = prev.filter(req => req.id !== request.id);
            console.log('🎤 Removed performed request from myRequests:', request.id, 'Remaining:', filtered.length);
            return filtered;
          });
        });
      },
      (error) => {
        console.error('❌ KaraokePublicPage: Error listening to performed requests:', error);
      }
    );

    return () => {
      console.log('🧹 KaraokePublicPage: Cleaning up Firestore listeners');
      liveStatusUnsubscribe();
      approvedRequestsUnsubscribe();
      pendingRequestsUnsubscribe();
      deniedRequestsUnsubscribe();
      performedRequestsUnsubscribe();
      
      // Decrement viewer count when page unmounts (if karaoke was live and we tracked a viewer)
      if (artist?.uid && liveStatus?.is_live && hasTrackedViewer) {
        try {
          const liveStatusRef = doc(db, 'live_status', artist.uid);
          updateDoc(liveStatusRef, {
            viewer_count: increment(-1),
            last_viewer_update: serverTimestamp()
          }).catch(error => {
            console.error('❌ Error decrementing viewer count:', error);
          });
          console.log('✅ Viewer count decremented for karaoke:', artist.uid);
        } catch (error) {
          console.error('❌ Error decrementing viewer count:', error);
        }
      }
    };
  }, [artist]);

  // Show non-blocking announcement banner for all viewers.
  useEffect(() => {
    const announcement = liveStatus?.up_next_announcement || null;
    setGlobalUpNextAnnouncement(announcement);
  }, [liveStatus?.up_next_announcement]);

  useEffect(() => {
    if (!globalUpNextAnnouncement) {
      setMobileAnnouncementHiddenKey(null);
    }
  }, [globalUpNextAnnouncement]);

  useEffect(() => {
    if (!liveStatus?.is_live || !globalUpNextAnnouncement) return;
    const instanceKey = getKaraokeAnnouncementInstanceKey(globalUpNextAnnouncement);
    if (!instanceKey || mobileAnnouncementHiddenKey === instanceKey) return;

    const t = window.setTimeout(() => {
      setMobileAnnouncementHiddenKey(instanceKey);
    }, 10_000);
    return () => window.clearTimeout(t);
  }, [
    liveStatus?.is_live,
    globalUpNextAnnouncement,
    getKaraokeAnnouncementInstanceKey,
    mobileAnnouncementHiddenKey
  ]);

  const visibleMobileUpNextAnnouncement = useMemo(() => {
    if (!liveStatus?.is_live || !globalUpNextAnnouncement) return null;
    const k = getKaraokeAnnouncementInstanceKey(globalUpNextAnnouncement);
    if (!k || mobileAnnouncementHiddenKey === k) return null;
    return globalUpNextAnnouncement;
  }, [
    liveStatus?.is_live,
    globalUpNextAnnouncement,
    mobileAnnouncementHiddenKey,
    getKaraokeAnnouncementInstanceKey
  ]);

  const dismissMobileUpNextAnnouncement = useCallback(() => {
    if (!globalUpNextAnnouncement) return;
    const k = getKaraokeAnnouncementInstanceKey(globalUpNextAnnouncement);
    if (k) setMobileAnnouncementHiddenKey(k);
  }, [globalUpNextAnnouncement, getKaraokeAnnouncementInstanceKey]);

  // Keep announcement non-blocking for all viewers; no modal interruption.

  // Handle payment success for skip queue - submit request if fields are filled, otherwise show form
  const handlePaymentSuccess = async (paymentData) => {
    console.log('✅ Payment successful:', paymentData);
    
    // Close payment modal
    setShowPaymentModal(false);
    setShowSkipQueueModal(false);
    
    // Check if required fields are filled
    let fieldsFilled = false;
    
    if (useCustomForm && customFormFields) {
      const missingFields = customFormFields
        .filter(field => field.required)
        .filter(field => {
          const value = requestForm[field.id];
          return !value || (typeof value === 'string' && value.trim() === '');
        });
      fieldsFilled = missingFields.length === 0;
    } else {
      fieldsFilled = !!(requestForm.song_title && requestForm.artist_name && requestForm.requester_name);
    }
    
    if (!fieldsFilled) {
      // Fields not filled - store payment data and show form
      setPendingPaymentData(paymentData);
      setRequestForm(prev => ({
        ...prev,
        skip_queue: true,
        amount_paid: paymentData.amount,
        transaction_reference: paymentData.transactionReference,
        order_reference: paymentData.orderReference
      }));
      
      toast({
        title: isPayPalPaymentProvider(paymentData?.paymentProvider) ? 'Payment received' : 'Payment Successful!',
        description:
          isPayPalPaymentProvider(paymentData?.paymentProvider)
            ? `Your payment in ${artistCurrency} is confirmed. Add your song details below to finish.`
            : 'Please fill in the song details below to complete your request.',
        variant: 'default',
      });
      
      // Show request form modal
      setShowRequestModal(true);
      return;
    }
    
    // Fields are filled - proceed with automatic submission
    // Set skip_queue flag and amount_paid
    setRequestForm(prev => ({
      ...prev,
      skip_queue: true,
      amount_paid: paymentData.amount,
      transaction_reference: paymentData.transactionReference,
      order_reference: paymentData.orderReference
    }));
    
    // Automatically submit the request (like Artist Mode)
    // Create a synthetic event for handleRequestSubmit
    const syntheticEvent = { preventDefault: () => {} };
    
    // Set submitting state and submit
    setSubmitting(true);
    
    try {

      const sessionId = (await resolveLiveSessionId()) ?? null;

      // Create request data with payment information
      const requestData = {
        karaoke_host_id: artist.uid,
        status: 'pending',
        created_at: new Date().toISOString(),
        session_id: sessionId,
        skip_queue: true,
        amount_paid: paymentData.amount,
        transaction_reference: paymentData.transactionReference,
        order_reference: paymentData.orderReference,
        payment_status: 'succeeded'
      };

      // Add all form fields to request data
      if (useCustomForm && customFormFields) {
        customFormFields.forEach(field => {
          requestData[field.id] = requestForm[field.id] || (field.type === 'checkbox' ? false : '');
        });
        
        // Ensure essential fields are always included
        if (!requestData.song_title) requestData.song_title = requestForm.song_title || '';
        if (!requestData.artist_name) requestData.artist_name = requestForm.artist_name || '';
        if (!requestData.requester_name) requestData.requester_name = requestForm.requester_name || '';
        if (!requestData.note) requestData.note = requestForm.note || '';
      } else {
        requestData.song_title = requestForm.song_title;
        requestData.artist_name = requestForm.artist_name;
        requestData.requester_name = requestForm.requester_name;
        requestData.note = requestForm.note || '';
      }

      // The payment backend creates the paid karaoke request server-side (with
      // the verified payment reference), so we must not insert a second copy —
      // that put two identical entries in the host's queue. Find that document
      // instead, so it still shows under "My Requests".
      debugLog('RECEIVE_REQUEST', 'Paid karaoke skip request awaiting server record', {
        artistId: artist.uid,
        songTitle: requestData.song_title,
        requesterName: requestData.requester_name,
        amountPaid: paymentData.amount,
        mode: 'karaoke'
      });

      const serverRequest = await findServerCreatedKaraokeRequest(
        paymentData.transactionReference
      );

      if (sessionId) {
        trackSongInteraction(artist.uid, 'manual_entry', 'requested', sessionId);
        if (isPaidSongRequestForLedger(requestData)) {
          trackSongInteraction(artist.uid, 'manual_entry', 'paid', sessionId);
        }
      }

      if (serverRequest) {
        sessionManager.addRequest(serverRequest.id, serverRequest);
        setMyRequests(prev =>
          prev.some(req => req.id === serverRequest.id) ? prev : [...prev, serverRequest]
        );
        debugLog('SUCCESS', 'Paid karaoke request confirmed from server record', {
          requestId: serverRequest.id,
          artistId: artist.uid,
          mode: 'karaoke'
        });
      } else {
        // The queue listener will pick it up shortly; don't create a duplicate.
        console.warn('Paid karaoke request not visible yet; it will appear once the gateway finalises.');
      }
      
      // Close request modal
      setShowRequestModal(false);
      
      toast({
        title:
          isPayPalPaymentProvider(paymentData?.paymentProvider)
            ? 'You’re all set!'
            : 'Payment Successful & Request Sent!',
        description:
          isPayPalPaymentProvider(paymentData?.paymentProvider)
            ? `${format(paymentData.amount)} received. Your request for "${requestData.song_title}" will skip the queue.`
            : `${format(paymentData.amount)} paid. Your request for "${requestData.song_title}" has been sent and will skip the queue!`,
        variant: 'default',
      });
      
      // Reset form
      resetForm();
    } catch (error) {
      console.error('❌ Exception submitting request after payment:', error);
      toast({ 
        title: "Payment Successful, But Request Failed", 
        description: "Your payment was successful, but there was an error submitting your request. Please contact support.", 
        variant: "destructive" 
      });
      // Show form so they can try again
      setShowRequestModal(true);
    } finally {
      setSubmitting(false);
    }
  };

  // Handle payment cancel
  const handlePaymentCancel = () => {
    setShowPaymentModal(false);
  };

  const handleRequestSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    console.log('🎤 Submitting karaoke request:', requestForm);
    console.log('🎤 Artist object:', artist);
    console.log('🎤 Artist ID:', artist?.id);

    if (!artist || !artist.uid) {
      console.error('❌ No artist or artist UID found!');
      toast({ title: "Error", description: "Artist information not found. Please refresh the page.", variant: "destructive" });
      setSubmitting(false);
      return;
    }

    // Validate required fields based on form type
    if (useCustomForm && customFormFields) {
      // Validate custom form required fields
      const missingFields = customFormFields
        .filter(field => field.required)
        .filter(field => {
          const value = requestForm[field.id];
          return !value || (typeof value === 'string' && value.trim() === '');
        });
      
      if (missingFields.length > 0) {
        toast({ 
          title: "Missing Required Fields", 
          description: `Please fill in: ${missingFields.map(f => f.label).join(', ')}`, 
          variant: "destructive" 
        });
        setSubmitting(false);
        return;
      }
    } else {
      // Validate default form required fields
      if (!requestForm.song_title || !requestForm.artist_name || !requestForm.requester_name) {
        toast({ title: "Error", description: "Please fill in all required fields for song request.", variant: "destructive" });
        setSubmitting(false);
        return;
      }
    }

    try {
      // ✅ Check if list is full and request is not paid
      if (liveStatus?.list_is_full && !requestForm.skip_queue && !pendingPaymentData) {
        const checkBackMinutes = liveStatus?.check_back_minutes || 15;
        toast({
          title: "List Is Full",
          description: `The request list is currently full. Please check back in ${checkBackMinutes} minutes, or use 'Skip The Queue' to submit a paid request.`,
          variant: "destructive"
        });
        setSubmitting(false);
        setShowRequestModal(true); // Keep modal open so they can choose to pay
        return;
      }

      // ✅ Check if paid requests only is enabled and request is not paid
      if (paidRequestSettings?.paidRequestsOnly && !requestForm.skip_queue && !pendingPaymentData) {
        toast({
          title: "Paid Requests Only",
          description: "This karaoke host is currently only accepting paid requests. Please use 'Skip The Queue' to submit your request.",
          variant: "destructive"
        });
        setSubmitting(false);
        setShowRequestModal(true); // Keep modal open so they can choose to pay
        return;
      }

      // Check if there's pending payment data (payment made before form was filled)
      if (pendingPaymentData) {
        // Apply pending payment data to form
        setRequestForm(prev => ({
          ...prev,
          skip_queue: true,
          amount_paid: pendingPaymentData.amount,
          payment_intent_id: pendingPaymentData.paymentIntentId,
          payment_method_id: pendingPaymentData.paymentMethodId
        }));
      }
      
      const sessionId = (await resolveLiveSessionId()) ?? null;

      // Create base request data
      const requestData = {
        karaoke_host_id: artist.uid,
        status: 'pending',
        created_at: new Date().toISOString(),
        session_id: sessionId,
      };

      // Add all form fields to request data
      if (useCustomForm && customFormFields) {
        // Add all custom form fields
        customFormFields.forEach(field => {
          requestData[field.id] = requestForm[field.id] || (field.type === 'checkbox' ? false : '');
        });
        
        // Ensure essential fields are always included, even if not in custom form
        if (!requestData.song_title) requestData.song_title = requestForm.song_title || '';
        if (!requestData.artist_name) requestData.artist_name = requestForm.artist_name || '';
        if (!requestData.requester_name) requestData.requester_name = requestForm.requester_name || '';
        if (!requestData.note) requestData.note = requestForm.note || '';
        if (requestData.skip_queue === undefined) requestData.skip_queue = requestForm.skip_queue || false;
        
        // Add payment information if payment was made (either from form or pending payment)
        if ((requestForm.skip_queue || pendingPaymentData) && (requestForm.amount_paid > 0 || pendingPaymentData?.amount > 0)) {
          requestData.amount_paid = requestForm.amount_paid || pendingPaymentData.amount;
          requestData.payment_intent_id = requestForm.payment_intent_id || pendingPaymentData.paymentIntentId;
          requestData.payment_method_id = requestForm.payment_method_id || pendingPaymentData.paymentMethodId;
          requestData.payment_status = 'succeeded';
          requestData.skip_queue = true; // Ensure skip_queue is set if payment was made
        }
      } else {
        // Add default form fields
        requestData.song_title = requestForm.song_title;
        requestData.artist_name = requestForm.artist_name;
        requestData.requester_name = requestForm.requester_name;
        requestData.note = requestForm.note;
        requestData.skip_queue = requestForm.skip_queue || (pendingPaymentData ? true : false);
        
        // Add payment information if payment was made (either from form or pending payment)
        if ((requestForm.skip_queue || pendingPaymentData) && (requestForm.amount_paid > 0 || pendingPaymentData?.amount > 0)) {
          requestData.amount_paid = requestForm.amount_paid || pendingPaymentData.amount;
          requestData.payment_intent_id = requestForm.payment_intent_id || pendingPaymentData.paymentIntentId;
          requestData.payment_method_id = requestForm.payment_method_id || pendingPaymentData.paymentMethodId;
          requestData.payment_status = 'succeeded';
          requestData.skip_queue = true; // Ensure skip_queue is set if payment was made
        }
      }

      console.log('🎤 Inserting request data:', requestData);
      debugLog('RECEIVE_REQUEST', 'New karaoke song request received', { 
        artistId: artist.uid, 
        songTitle: requestData.song_title,
        requesterName: requestData.requester_name,
        mode: 'karaoke'
      });

      const docRef = await addDoc(collection(db, 'karaoke_requests'), requestData);

      // Track interaction (using manual_entry as songId for karaoke)
      if (sessionId) {
        trackSongInteraction(artist.uid, 'manual_entry', 'requested', sessionId);
        if (isPaidSongRequestForLedger(requestData)) {
          trackSongInteraction(artist.uid, 'manual_entry', 'paid', sessionId);
        }
      }

      console.log('✅ Request submitted successfully:', docRef.id);
      debugLog('SUCCESS', 'Karaoke song request saved to Firestore successfully', { 
        requestId: docRef.id, 
        artistId: artist.uid, 
        mode: 'karaoke'
      });
      
      // Store request in session for tracking with immediate status
      const requestWithId = {
        ...requestData,
        id: docRef.id,
        firestoreId: docRef.id,
        status: 'pending',
        created_at: new Date().toISOString()
      };
      
      sessionManager.addRequest(docRef.id, requestWithId);
      
      // Immediately add to myRequests for instant UI update
      setMyRequests(prev => {
        const existing = prev.find(req => req.id === docRef.id);
        if (!existing) {
          return [...prev, requestWithId];
        }
        return prev;
      });
      
      toast({
        title: pendingPaymentData
          ? isPayPalPaymentProvider(pendingPaymentData.paymentProvider)
            ? 'You’re all set!'
            : 'Payment Successful & Request Sent!'
          : 'Request Sent!',
        description: pendingPaymentData
          ? isPayPalPaymentProvider(pendingPaymentData.paymentProvider)
            ? `${format(pendingPaymentData.amount || requestForm.amount_paid || 0)} received. Your request for "${requestForm.song_title}" will skip the queue.`
            : `${format(pendingPaymentData.amount || requestForm.amount_paid || 0)} paid. Your request for "${requestForm.song_title}" has been sent and will skip the queue!`
          : `Your request for "${requestForm.song_title}" has been sent! The karaoke host will review it shortly.`,
        variant: 'default',
      });
      
      // Clear pending payment data if it exists
      if (pendingPaymentData) {
        setPendingPaymentData(null);
      }
      
      // Reset form and close modal
      resetForm();
      setShowRequestModal(false);
    } catch (error) {
      console.error('❌ Exception submitting request:', error);
      toast({ 
        title: "Error", 
        description: "An unexpected error occurred. Please try again.", 
        variant: "destructive" 
      });
    }

    setSubmitting(false);
  };

  const resetForm = () => {
    // Clear pending payment data
    setPendingPaymentData(null);
    
    if (useCustomForm && customFormFields) {
      // Reset custom form fields
      const resetForm = {};
      customFormFields.forEach(field => {
        resetForm[field.id] = field.type === 'checkbox' ? false : '';
      });
      setRequestForm(prev => ({ ...prev, ...resetForm, skip_queue: false, amount_paid: 0 }));
    } else {
      // Reset default form fields
      setRequestForm({
        song_title: '',
        artist_name: '',
        requester_name: '',
        note: '',
        skip_queue: false,
        amount_paid: 0
      });
    }
  };

  // Filter requests that belong to this session
  const filterMyRequests = (allRequests) => {
    const myRequests = sessionManager.getRequests();
    const myRequestIds = Object.keys(myRequests);
    console.log('🎤 Session Manager requests:', myRequests);
    console.log('🎤 My request IDs:', myRequestIds);
    console.log('🎤 All requests to filter:', allRequests);
    
    const filtered = allRequests.filter(request => myRequestIds.includes(request.id));
    console.log('🎤 Filtered my requests:', filtered);
    return filtered;
  };

  // Get queue position for a request
  const getQueuePosition = (requestId) => {
    // First try to get from session manager (most up-to-date)
    const storedRequest = sessionManager.getRequest(requestId);
    if (storedRequest && storedRequest.queuePosition) {
      return storedRequest.queuePosition;
    }
    
    // Fallback to current requests array
    const position = requests.findIndex(req => req.id === requestId);
    return position >= 0 ? position + 1 : null;
  };



  // CRITICAL: Only show "Not Found" if we've finished loading AND artist is still null
  // This prevents showing "Not Found" during initial load or while fetching
  if (loading || !profileLookupAttempted) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-purple-500 mx-auto mb-4"></div>
          <p className="text-xl">Loading karaoke page...</p>
        </div>
      </div>
    );
  }

  // Only show "Not Found" if we've attempted to load AND artist is null
  if (!artist && profileLookupAttempted && !loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Karaoke Host Not Found</h1>
          <p className="text-gray-400 mb-4">The karaoke host you're looking for doesn't exist.</p>
          <p className="text-sm text-gray-500">URL: {window.location.pathname}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{artist.karaoke_name || artist.artist_name} - Karaoke Night - Your Song Request</title>
        <meta name="description" content={`Join ${artist.karaoke_name || artist.artist_name} for an amazing karaoke night! Request songs and show your support.`} />
      </Helmet>

      <div 
        className="min-h-screen text-white relative overflow-hidden"
        style={isChristmasTheme ? {
          background: '#0a0e1a',
          backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(220, 38, 38, 0.15) 0%, transparent 50%), radial-gradient(circle at 80% 50%, rgba(22, 163, 74, 0.15) 0%, transparent 50%)'
        } : {
          background: 'linear-gradient(to bottom right, #6b21a8, #7e22ce, #6366f1)'
        }}
      >
        {/* ✅ CHRISTMAS THEME: Immersive Snowstorm */}
        {isChristmasTheme && (
          <>
            {/* Starry sky */}
            {Array.from({ length: 40 }, (_, i) => (
              <div
                key={`star-form-${i}`}
                className="absolute rounded-full bg-white animate-twinkle-form"
                style={{
                  left: `${(i * 31) % 100}%`,
                  top: `${(i * 27) % 100}%`,
                  width: `${2 + (i % 3)}px`,
                  height: `${2 + (i % 3)}px`,
                  animationDelay: `${i * 0.3}s`,
                  animationDuration: `${2 + (i % 4)}s`,
                  boxShadow: '0 0 4px rgba(255, 255, 255, 0.8)'
                }}
              />
            ))}
            
            {/* Heavy snowfall - multiple layers */}
            {Array.from({ length: 80 }, (_, i) => (
              <div
                key={`snow-form-close-${i}`}
                className="absolute text-white animate-snowfall-form-close"
                style={{
                  left: `${(i * 19) % 100}%`,
                  animationDelay: `${i * 0.15}s`,
                  animationDuration: `${3 + (i % 4)}s`,
                  fontSize: `${12 + (i % 6)}px`,
                  opacity: 0.95,
                  zIndex: 100
                }}
              >
                ❄️
              </div>
            ))}
            {Array.from({ length: 60 }, (_, i) => (
              <div
                key={`snow-form-mid-${i}`}
                className="absolute text-white animate-snowfall-form-mid"
                style={{
                  left: `${(i * 27) % 100}%`,
                  animationDelay: `${i * 0.25}s`,
                  animationDuration: `${5 + (i % 5)}s`,
                  fontSize: `${7 + (i % 5)}px`,
                  opacity: 0.8,
                  zIndex: 50
                }}
              >
                ❄️
              </div>
            ))}
            {Array.from({ length: 40 }, (_, i) => (
              <div
                key={`snow-form-far-${i}`}
                className="absolute text-white animate-snowfall-form-far"
                style={{
                  left: `${(i * 33) % 100}%`,
                  animationDelay: `${i * 0.35}s`,
                  animationDuration: `${7 + (i % 6)}s`,
                  fontSize: `${4 + (i % 4)}px`,
                  opacity: 0.6,
                  zIndex: 10
                }}
              >
                ❄️
              </div>
            ))}
            
            {/* Snow dots */}
            {Array.from({ length: 120 }, (_, i) => (
              <div
                key={`snow-dot-form-${i}`}
                className="absolute rounded-full bg-white animate-snowfall-dot-form"
                style={{
                  left: `${(i * 23) % 100}%`,
                  animationDelay: `${i * 0.1}s`,
                  animationDuration: `${4 + (i % 8)}s`,
                  width: '2px',
                  height: '2px',
                  opacity: 0.7,
                  boxShadow: '0 0 2px rgba(255, 255, 255, 0.8)'
                }}
              />
            ))}
            
            <style>{`
              @keyframes twinkle-form {
                0%, 100% { opacity: 0.3; transform: scale(1); }
                50% { opacity: 1; transform: scale(1.3); }
              }
              @keyframes snowfall-form-close {
                0% {
                  transform: translateY(-50px) translateX(0) rotate(0deg) scale(1);
                  opacity: 0;
                }
                5% { opacity: 0.95; }
                50% {
                  transform: translateX(${Math.random() * 60 - 30}px) rotate(180deg) scale(1.1);
                }
                95% { opacity: 0.95; }
                100% {
                  transform: translateY(calc(100vh + 50px)) translateX(${Math.random() * 80 - 40}px) rotate(360deg) scale(0.9);
                  opacity: 0;
                }
              }
              @keyframes snowfall-form-mid {
                0% {
                  transform: translateY(-40px) translateX(0) rotate(0deg);
                  opacity: 0;
                }
                8% { opacity: 0.8; }
                50% {
                  transform: translateX(${Math.random() * 40 - 20}px) rotate(-180deg);
                }
                92% { opacity: 0.8; }
                100% {
                  transform: translateY(calc(100vh + 40px)) translateX(${Math.random() * 60 - 30}px) rotate(360deg);
                  opacity: 0;
                }
              }
              @keyframes snowfall-form-far {
                0% {
                  transform: translateY(-30px) translateX(0) rotate(0deg);
                  opacity: 0;
                }
                10% { opacity: 0.6; }
                50% {
                  transform: translateX(${Math.random() * 20 - 10}px) rotate(90deg);
                }
                90% { opacity: 0.6; }
                100% {
                  transform: translateY(calc(100vh + 30px)) translateX(${Math.random() * 40 - 20}px) rotate(180deg);
                  opacity: 0;
                }
              }
              @keyframes snowfall-dot-form {
                0% {
                  transform: translateY(-20px) translateX(0);
                  opacity: 0;
                }
                10% { opacity: 0.7; }
                50% {
                  transform: translateX(${Math.random() * 15 - 7.5}px);
                }
                90% { opacity: 0.7; }
                100% {
                  transform: translateY(calc(100vh + 20px)) translateX(${Math.random() * 25 - 12.5}px);
                  opacity: 0;
                }
              }
              .animate-twinkle-form {
                animation: twinkle-form ease-in-out infinite;
              }
              .animate-snowfall-form-close {
                animation: snowfall-form-close linear infinite;
              }
              .animate-snowfall-form-mid {
                animation: snowfall-form-mid linear infinite;
              }
              .animate-snowfall-form-far {
                animation: snowfall-form-far linear infinite;
              }
              .animate-snowfall-dot-form {
                animation: snowfall-dot-form linear infinite;
              }
            `}</style>
          </>
        )}

        {/* Header Image */}
        {artist.header_image_url && (
          <div className="w-full h-64 md:h-80 lg:h-96 relative overflow-hidden">
            <img 
              src={artist.header_image_url} 
              alt={`${artist.karaoke_name || artist.artist_name} header`}
              className="w-full h-full object-cover"
            />
            <div className={`absolute inset-0 bg-gradient-to-b from-transparent ${
              isChristmasTheme 
                ? 'via-red-900/20 to-green-900/60' 
                : 'via-purple-900/20 to-purple-900/60'
            }`} />
          </div>
        )}
        
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {/* ✅ CHRISTMAS THEME: Immersive Christmas Wonderland */}
          {isChristmasTheme ? (
            <>
              {/* Floating Christmas Trees */}
              {Array.from({ length: 6 }, (_, i) => (
                <div 
                  key={`tree-form-${i}`}
                  className="absolute animate-float-tree-form"
                  style={{
                    left: `${15 + (i * 15)}%`,
                    bottom: `${(i % 2) * 10}%`,
                    animationDelay: `${i * 0.6}s`,
                    animationDuration: `${5 + (i % 3)}s`,
                    fontSize: `${50 + (i % 3) * 15}px`,
                    filter: 'drop-shadow(0 0 15px rgba(22, 163, 74, 0.7))',
                    zIndex: 30
                  }}
                >
                  🎄
                </div>
              ))}
              
              {/* Floating Presents */}
              {Array.from({ length: 10 }, (_, i) => (
                <div 
                  key={`gift-form-${i}`}
                  className="absolute animate-float-gift-form"
                  style={{
                    left: `${(i * 10) % 100}%`,
                    top: `${15 + (i % 4) * 20}%`,
                    animationDelay: `${i * 0.4}s`,
                    animationDuration: `${6 + (i % 4)}s`,
                    fontSize: `${35 + (i % 3) * 10}px`,
                    opacity: 0.9,
                    zIndex: 35
                  }}
                >
                  🎁
                </div>
              ))}
              
              {/* Twinkling Stars */}
              {Array.from({ length: 12 }, (_, i) => (
                <div 
                  key={`star-form-float-${i}`}
                  className="absolute animate-twinkle-star-form"
                  style={{
                    left: `${(i * 8) % 100}%`,
                    top: `${8 + (i % 5) * 18}%`,
                    animationDelay: `${i * 0.5}s`,
                    animationDuration: `${2.5 + (i % 3)}s`,
                    fontSize: `${25 + (i % 4) * 6}px`,
                    filter: 'drop-shadow(0 0 10px rgba(251, 191, 36, 0.9))',
                    zIndex: 25
                  }}
                >
                  ⭐
                </div>
              ))}
              
              {/* Santa and Friends */}
              <div className="absolute top-20 left-8 animate-santa-form" style={{ fontSize: '40px', zIndex: 40 }}>
                🎅
              </div>
              <div className="absolute top-30 left-12 animate-reindeer-form" style={{ fontSize: '35px', zIndex: 39, animationDelay: '0.3s' }}>
                🦌
              </div>
              <div className="absolute bottom-20 right-12 animate-bell-form" style={{ fontSize: '45px', zIndex: 38 }}>
                🔔
              </div>
              
              {/* Animated Christmas Lights Strings - Top */}
              <div className="absolute top-0 left-0 w-full h-8 flex items-center justify-center gap-2" style={{ zIndex: 200 }}>
                {Array.from({ length: 50 }, (_, i) => (
                  <div
                    key={`light-string-top-${i}`}
                    className="w-4 h-4 rounded-full animate-light-twinkle-form"
                    style={{
                      backgroundColor: ['#dc2626', '#16a34a', '#ffffff', '#fbbf24', '#dc2626', '#16a34a'][i % 6],
                      animationDelay: `${i * 0.12}s`,
                      animationDuration: '2s',
                      boxShadow: '0 0 10px currentColor, 0 0 15px currentColor',
                      filter: 'brightness(1.3)'
                    }}
                  />
                ))}
              </div>
              
              {/* Animated Christmas Lights Strings - Bottom */}
              <div className="absolute bottom-0 left-0 w-full h-8 flex items-center justify-center gap-2" style={{ zIndex: 200 }}>
                {Array.from({ length: 50 }, (_, i) => (
                  <div
                    key={`light-string-bottom-${i}`}
                    className="w-4 h-4 rounded-full animate-light-twinkle-form"
                    style={{
                      backgroundColor: ['#16a34a', '#dc2626', '#fbbf24', '#ffffff', '#16a34a', '#dc2626'][i % 6],
                      animationDelay: `${i * 0.12 + 1}s`,
                      animationDuration: '2s',
                      boxShadow: '0 0 10px currentColor, 0 0 15px currentColor',
                      filter: 'brightness(1.3)'
                    }}
                  />
                ))}
              </div>
              
              {/* Side Garland Lights */}
              <div className="absolute left-0 top-1/3 w-10 flex flex-col items-center gap-5" style={{ zIndex: 150 }}>
                {Array.from({ length: 15 }, (_, i) => (
                  <div
                    key={`garland-left-form-${i}`}
                    className="w-5 h-5 rounded-full animate-light-twinkle-form"
                    style={{
                      backgroundColor: ['#dc2626', '#16a34a', '#ffffff', '#fbbf24'][i % 4],
                      animationDelay: `${i * 0.25}s`,
                      boxShadow: '0 0 8px currentColor'
                    }}
                  />
                ))}
              </div>
              
              <div className="absolute right-0 top-1/3 w-10 flex flex-col items-center gap-5" style={{ zIndex: 150 }}>
                {Array.from({ length: 15 }, (_, i) => (
                  <div
                    key={`garland-right-form-${i}`}
                    className="w-5 h-5 rounded-full animate-light-twinkle-form"
                    style={{
                      backgroundColor: ['#16a34a', '#dc2626', '#fbbf24', '#ffffff'][i % 4],
                      animationDelay: `${i * 0.25 + 0.6}s`,
                      boxShadow: '0 0 8px currentColor'
                    }}
                  />
                ))}
              </div>
              
              <style>{`
                @keyframes float-tree-form {
                  0%, 100% {
                    transform: translateY(0) rotate(0deg);
                  }
                  50% {
                    transform: translateY(-20px) rotate(3deg);
                  }
                }
                @keyframes float-gift-form {
                  0%, 100% {
                    transform: translateY(0) translateX(0) rotate(0deg);
                  }
                  25% {
                    transform: translateY(-12px) translateX(6px) rotate(8deg);
                  }
                  75% {
                    transform: translateY(-6px) translateX(-6px) rotate(-8deg);
                  }
                }
                @keyframes twinkle-star-form {
                  0%, 100% {
                    opacity: 0.7;
                    transform: scale(1);
                  }
                  50% {
                    opacity: 1;
                    transform: scale(1.4);
                  }
                }
                @keyframes santa-form {
                  0% {
                    transform: translateX(0) translateY(0);
                  }
                  33% {
                    transform: translateX(12px) translateY(-6px);
                  }
                  66% {
                    transform: translateX(-6px) translateY(6px);
                  }
                  100% {
                    transform: translateX(0) translateY(0);
                  }
                }
                @keyframes reindeer-form {
                  0%, 100% {
                    transform: translateX(0) rotate(0deg);
                  }
                  50% {
                    transform: translateX(10px) rotate(-4deg);
                  }
                }
                @keyframes bell-form {
                  0%, 100% {
                    transform: translateY(0) rotate(0deg);
                  }
                  50% {
                    transform: translateY(-15px) rotate(20deg);
                  }
                }
                @keyframes light-twinkle-form {
                  0%, 100% {
                    opacity: 0.5;
                    transform: scale(0.95);
                  }
                  50% {
                    opacity: 1;
                    transform: scale(1.15);
                  }
                }
                .animate-float-tree-form {
                  animation: float-tree-form ease-in-out infinite;
                }
                .animate-float-gift-form {
                  animation: float-gift-form ease-in-out infinite;
                }
                .animate-twinkle-star-form {
                  animation: twinkle-star-form ease-in-out infinite;
                }
                .animate-santa-form {
                  animation: santa-form ease-in-out infinite;
                }
                .animate-reindeer-form {
                  animation: reindeer-form ease-in-out infinite;
                }
                .animate-bell-form {
                  animation: bell-form ease-in-out infinite;
                }
                .animate-light-twinkle-form {
                  animation: light-twinkle-form ease-in-out infinite;
                }
              `}</style>
            </>
          ) : (
            <>
              {/* Floating Music Notes */}
              <div className="absolute top-20 left-10 animate-bounce" style={{ animationDelay: '0s', animationDuration: '6s' }}>
                <span className="text-4xl text-purple-400/30">♪</span>
              </div>
              <div className="absolute top-40 right-20 animate-bounce" style={{ animationDelay: '1s', animationDuration: '7s' }}>
                <span className="text-3xl text-pink-400/30">♫</span>
              </div>
              <div className="absolute top-60 left-1/4 animate-bounce" style={{ animationDelay: '2s', animationDuration: '6.5s' }}>
                <span className="text-2xl text-indigo-400/30">🎵</span>
              </div>
              <div className="absolute bottom-40 right-1/3 animate-bounce" style={{ animationDelay: '3s', animationDuration: '8s' }}>
                <span className="text-3xl text-purple-400/30">🎤</span>
              </div>
              <div className="absolute bottom-20 left-1/3 animate-bounce" style={{ animationDelay: '4s', animationDuration: '5.5s' }}>
                <span className="text-2xl text-pink-400/30">🎶</span>
              </div>
              
              {/* Animated Gradient Orbs */}
              <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-gradient-to-r from-purple-600/10 to-pink-600/10 rounded-full blur-3xl animate-pulse"></div>
              <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-gradient-to-r from-indigo-600/10 to-purple-600/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
            </>
          )}
        </div>
        {/* Header */}
        <div className={`bg-black/20 backdrop-blur-sm border-b ${
          isChristmasTheme 
            ? 'border-red-500/50' 
            : 'border-purple-500/30'
        }`}>
          <div className="max-w-4xl mx-auto px-4 py-6">
            <div className="text-center">
              <motion.div 
                className="flex items-center justify-center space-x-4 mb-4"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <motion.div 
                  className={`w-16 h-16 rounded-full flex items-center justify-center shadow-lg ${
                    isChristmasTheme 
                      ? 'bg-gradient-to-r from-red-600 to-green-600' 
                      : 'bg-gradient-to-r from-purple-600 to-pink-600'
                  }`}
                  whileHover={{ scale: 1.1, rotate: 5 }}
                  animate={{ 
                    boxShadow: isChristmasTheme ? [
                      "0 0 20px rgba(220, 38, 38, 0.5)",
                      "0 0 30px rgba(22, 163, 74, 0.5)",
                      "0 0 20px rgba(251, 191, 36, 0.5)",
                      "0 0 30px rgba(22, 163, 74, 0.5)",
                      "0 0 20px rgba(220, 38, 38, 0.5)"
                    ] : [
                      "0 0 20px rgba(147, 51, 234, 0.5)",
                      "0 0 30px rgba(236, 72, 153, 0.5)",
                      "0 0 20px rgba(147, 51, 234, 0.5)"
                    ]
                  }}
                  transition={{ 
                    boxShadow: { duration: 2, repeat: Infinity, ease: "easeInOut" }
                  }}
                >
                  {isChristmasTheme ? (
                    <span className="text-3xl">🎄</span>
                  ) : (
                    <Mic className="w-8 h-8 text-white" />
                  )}
                </motion.div>
                <div>
                  <motion.h1 
                    className="text-3xl font-bold text-white"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                  >
                    {artist.karaoke_name || artist.artist_name}
                  </motion.h1>
                  <motion.p 
                    className={isChristmasTheme ? 'text-green-300' : 'text-purple-300'}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: 0.4 }}
                  >
                    Karaoke Night
                  </motion.p>
                </div>
              </motion.div>
              
              {liveStatus?.is_live && (
                <motion.div 
                  className="flex items-center justify-center space-x-3 mb-4"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: 0.6 }}
                >
                  <motion.div 
                    className="flex items-center space-x-2 bg-red-600/20 backdrop-blur-sm rounded-full px-4 py-2 border border-red-500/50"
                    animate={{ 
                      boxShadow: [
                        "0 0 10px rgba(239, 68, 68, 0.3)",
                        "0 0 20px rgba(239, 68, 68, 0.5)",
                        "0 0 10px rgba(239, 68, 68, 0.3)"
                      ]
                    }}
                    transition={{ 
                      boxShadow: { duration: 1.5, repeat: Infinity, ease: "easeInOut" }
                    }}
                  >
                    <motion.div 
                      className="w-3 h-3 bg-red-500 rounded-full"
                      animate={{ 
                        scale: [1, 1.2, 1],
                        opacity: [0.7, 1, 0.7]
                      }}
                      transition={{ 
                        duration: 1.5, 
                        repeat: Infinity, 
                        ease: "easeInOut" 
                      }}
                    />
                    <span className="text-red-400 font-semibold text-sm">LIVE NOW</span>
                  </motion.div>
                </motion.div>
              )}

              {/* List Is Full Banner */}
              {liveStatus?.is_live && liveStatus?.list_is_full && (
                <motion.div 
                  className="mt-4 mb-4"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.7 }}
                >
                  <div className="bg-yellow-900/40 backdrop-blur-sm border border-yellow-600/50 rounded-lg p-4 text-center">
                    <div className="flex items-center justify-center space-x-2 mb-2">
                      <span className="text-2xl">⚠️</span>
                      <h3 className="text-yellow-200 font-semibold text-lg">List Is Full</h3>
                    </div>
                    <p className="text-yellow-300 text-sm mb-2">
                      The request list is currently full. Please check back in {liveStatus?.check_back_minutes || 15} minutes.
                    </p>
                    <p className="text-yellow-400 text-xs">
                      You can still submit a paid request using "Skip The Queue" below.
                    </p>
                  </div>
                </motion.div>
              )}
              
              {/* Request a Song Button at Top - Only show when host is live */}
              {liveStatus?.is_live && (
                <motion.div 
                  className="mt-4"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.8 }}
                >
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Button
                      onClick={handleOpenRequestModal}
                      className={`text-white px-8 py-4 text-lg font-semibold shadow-lg ${
                        isChristmasTheme
                          ? 'bg-gradient-to-r from-red-600 via-green-600 to-red-600 hover:from-red-700 hover:via-green-700 hover:to-red-700 shadow-red-500/50'
                          : 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700'
                      }`}
                      size="lg"
                    >
                      <motion.div
                        animate={{ rotate: [0, 10, -10, 0] }}
                        transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                      >
                        {isChristmasTheme ? (
                          <span className="text-2xl mr-2">🎄</span>
                        ) : (
                          <Music className="w-5 h-5 mr-2" />
                        )}
                      </motion.div>
                      Request a Song
                      {isChristmasTheme && <span className="text-2xl ml-2">🎁</span>}
                    </Button>
                  </motion.div>
                </motion.div>
              )}

              {/* (Old announcement block removed to be placed below the button correctly) */}
            </div>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 py-8 relative min-h-[60vh]">
          {liveStatus?.is_live && visibleMobileUpNextAnnouncement && (
            <motion.div
              className="absolute inset-x-4 top-8 bottom-8 md:hidden z-30 rounded-2xl overflow-hidden shadow-2xl border border-purple-400/40 pointer-events-none"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.45 }}
            >
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  dismissMobileUpNextAnnouncement();
                }}
                className="pointer-events-auto absolute top-2 right-2 z-40 flex h-12 w-12 min-h-[48px] min-w-[48px] items-center justify-center rounded-full border border-white/25 bg-black/55 text-white shadow-lg backdrop-blur-sm touch-manipulation active:scale-95 transition-transform"
                aria-label="Dismiss announcement"
              >
                <X className="h-6 w-6" strokeWidth={2.5} />
              </button>
              <div className={`absolute inset-0 ${
                isChristmasTheme
                  ? 'bg-gradient-to-t from-red-950 via-yellow-900/80 to-red-950/90'
                  : 'bg-gradient-to-t from-purple-950 via-fuchsia-900/80 to-purple-950/90'
              } backdrop-blur-xl`} />
              <motion.div
                className="absolute -inset-y-10 -left-1/2 w-1/2 bg-white/20 blur-2xl"
                animate={{ x: ['0%', '280%'] }}
                transition={{ duration: 2.4, repeat: Infinity, ease: 'linear' }}
              />
              <motion.div
                className="absolute inset-0 opacity-40"
                animate={{ opacity: [0.25, 0.5, 0.25] }}
                transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
              >
                <div className="h-full w-full bg-[radial-gradient(circle_at_center,rgba(255,255,255,0.28)_0%,rgba(255,255,255,0)_62%)]" />
              </motion.div>

              <div className="absolute inset-0 flex items-center justify-center px-6 text-center">
                <div>
                  <motion.div
                    animate={{ scale: [1, 1.05, 1], rotate: [0, 1.5, -1.5, 0] }}
                    transition={{ duration: 2.2, repeat: Infinity, ease: 'easeInOut' }}
                    className="mb-6"
                  >
                    <span className="text-6xl sm:text-7xl drop-shadow-lg">🎤</span>
                  </motion.div>
                  <h3 className={`text-3xl sm:text-5xl font-extrabold tracking-widest mb-4 drop-shadow-[0_3px_10px_rgba(0,0,0,0.8)] ${
                    isChristmasTheme ? 'text-yellow-200' : 'text-purple-100'
                  }`}>
                    UP NEXT
                  </h3>
                  <p className={`text-3xl sm:text-4xl font-black leading-tight drop-shadow-[0_3px_10px_rgba(0,0,0,0.8)] ${
                    isChristmasTheme ? 'text-green-100' : 'text-white'
                  }`}>
                    {capitalizeWords(visibleMobileUpNextAnnouncement.song_title)}
                  </p>
                  <p className={`text-lg sm:text-2xl mt-2 font-semibold drop-shadow-md ${
                    isChristmasTheme ? 'text-green-200' : 'text-purple-200'
                  }`}>
                    by {capitalizeWords(visibleMobileUpNextAnnouncement.artist_name)}
                  </p>
                  <motion.div
                    initial={{ opacity: 0, scale: 0.92 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.45, delay: 0.15 }}
                    className={`mt-4 inline-flex flex-col items-center rounded-full px-6 py-3 shadow-xl ${
                      isChristmasTheme
                        ? 'bg-gradient-to-r from-red-600 via-yellow-500 to-green-600'
                        : 'bg-gradient-to-r from-purple-600 to-indigo-600'
                    }`}
                  >
                    <span className={`text-xs sm:text-sm font-semibold uppercase tracking-wide ${
                      isChristmasTheme ? 'text-white/95' : 'text-purple-100'
                    }`}>
                      requested by
                    </span>
                    <span className="text-lg sm:text-2xl font-bold text-white drop-shadow-md">
                      {capitalizeWords(
                        visibleMobileUpNextAnnouncement.requester_name ||
                          visibleMobileUpNextAnnouncement.name ||
                          'Guest'
                      )}
                    </span>
                  </motion.div>
                </div>
              </div>
            </motion.div>
          )}

          {liveStatus?.is_live ? (
                  <>
                    {/* Announcement now shown below the Request button as a non-blocking banner */}




                    {/* Your Requests Section - Compact */}
                    {myRequests.length > 0 && (
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                      >
                        <Card className={`backdrop-blur-sm mb-6 shadow-xl ${
                          isChristmasTheme
                            ? 'bg-red-900/20 border-red-500/50'
                            : 'bg-purple-800/20 border-purple-500/50'
                        }`}>
                          <CardHeader className="pb-3">
                            <CardTitle className={`flex items-center text-lg ${
                              isChristmasTheme ? 'text-green-200' : 'text-purple-200'
                            }`}>
                              <motion.div
                                animate={{ rotate: [0, 5, -5, 0] }}
                                transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                              >
                                <Music className="w-5 h-5 mr-2" />
                              </motion.div>
                              Your Requests
                            </CardTitle>
                          </CardHeader>
                        <CardContent className="pt-0">
                          <div className="space-y-2">
                            {myRequests.map((req, i) => {
                              const queuePosition = getQueuePosition(req.id);
                              return (
                                <motion.div key={req.id} initial={{opacity:0, x:-20}} animate={{opacity:1, x:0}} transition={{delay: i * 0.1}}>
                                  <div className={`flex justify-between items-center p-3 rounded-lg border ${
                                    req.status === 'approved' 
                                      ? 'bg-green-900/30 border-green-600/30' 
                                      : 'bg-blue-900/30 border-blue-600/30'
                                  }`}>
                                    <div className="flex-1">
                                      <div className="flex items-center space-x-2 mb-1">
                                        <p className={`font-bold ${req.status === 'approved' ? 'text-green-300' : 'text-blue-300'}`}>
                                          {capitalizeWords(req.song_title)}
                                        </p>
                                      </div>
                                      <p className={`text-sm ${req.status === 'approved' ? 'text-green-400' : 'text-blue-400'}`}>
                                        by {capitalizeWords(req.artist_name)}
                                      </p>

                                      {req.note && (
                                        <p className={`text-xs ${req.status === 'approved' ? 'text-green-500' : 'text-blue-500'} italic mt-1`}>
                                          Note: {req.note}
                                        </p>
                                      )}
                                      {req.skip_queue && (
                                        <p className={`text-xs ${req.status === 'approved' ? 'text-green-400' : 'text-blue-400'} font-semibold mt-1`}>
                                          💰 Paid Request - {format(req.amount_paid || paidRequestSettings.skipQueuePrice || 0)}
                                        </p>
                                      )}
                                    </div>
                                    <div className="flex flex-col items-end space-y-1">
                                      {req.status === 'approved' ? (
                                        <>
                                          <div className="flex items-center space-x-2">
                                            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                            <span className="text-green-400 text-sm font-semibold">Approved!</span>
                                          </div>
                                          {queuePosition && (
                                            <div className="bg-green-600 px-3 py-2 rounded-lg text-sm font-bold text-white">
                                              #{queuePosition} in queue
                                            </div>
                                          )}
                                        </>
                                      ) : (
                                        <>
                                          <div className="flex items-center space-x-2">
                                            <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></div>
                                            <span className="text-yellow-400 text-sm font-semibold">Pending Review</span>
                                          </div>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                </motion.div>
                              );
                            })}
                          </div>
                        </CardContent>
                      </Card>
                        </motion.div>
                    )}

                    {/* Request Queue Section - Always Show */}
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: 0.2 }}
                    >
                      <Card className={`backdrop-blur-sm mb-8 shadow-xl ${
                        isChristmasTheme
                          ? 'bg-red-900/20 border-red-500/50'
                          : 'bg-purple-800/20 border-purple-500/50'
                      }`}>
                      <CardHeader>
                        <CardTitle className={`flex items-center ${
                          isChristmasTheme ? 'text-green-200' : 'text-purple-200'
                        }`}>
                          <Users className="w-5 h-5 mr-2" />
                          Request Queue
                        </CardTitle>
                        <CardDescription className={isChristmasTheme ? 'text-green-300' : 'text-purple-300'}>
                          {requests.length > 0 ? `Songs in order (${requests.length} total)` : 'No requests yet, be the first!'}
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        {requests.length > 0 ? (
                          <div className="space-y-3">
                            {requests.slice(0, 3).map((req, i) => (
                              <motion.div key={req.id} initial={{opacity:0, x:-20}} animate={{opacity:1, x:0}} transition={{delay: i * 0.1}}>
                                <div className="flex justify-between items-center p-3 rounded-lg border bg-green-900/30 border-green-600/30">
                                  <div className="flex items-center space-x-3">
                                    <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center relative">
                                      <span className="text-white text-sm font-bold">{i + 1}</span>
                                      {req.skip_queue && (
                                        <div className="absolute -top-1 -right-1 w-4 h-4 bg-yellow-500 rounded-full flex items-center justify-center">
                                          <Star className="w-3 h-3 text-white" />
                                        </div>
                                      )}
                                    </div>
                                    <div className="flex-1">
                                                                              <div className="flex items-center space-x-2">
                                          <p className="font-bold text-green-300">
                                            {capitalizeWords(req.song_title)}
                                          </p>
                                        </div>
                                      <p className="text-sm text-green-400">
                                        by {capitalizeWords(req.artist_name)}
                                      </p>
                                    </div>
                                  </div>
                                  <div className="flex items-center space-x-2">
                                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                  </div>
                                </div>
                              </motion.div>
                                                          ))}
                            {requests.length > 3 && (
                              <>
                                {!showQueueExpanded ? (
                                  <button
                                    onClick={() => setShowQueueExpanded(true)}
                                    className={`text-center text-sm mt-3 w-full py-2 rounded-lg transition-colors ${
                                      isChristmasTheme
                                        ? 'text-green-300 hover:bg-green-900/30 hover:text-green-200'
                                        : 'text-purple-400 hover:bg-purple-900/30 hover:text-purple-300'
                                    }`}
                                  >
                                    +{requests.length - 3} more {requests.length - 3 === 1 ? 'song' : 'songs'} in the queue
                                  </button>
                                ) : (
                                  <div className="mt-3">
                                    <div className={`max-h-96 overflow-y-auto space-y-3 pr-2 ${
                                      isChristmasTheme ? 'scrollbar-green' : 'scrollbar-purple'
                                    }`} style={{
                                      scrollbarWidth: 'thin',
                                      scrollbarColor: isChristmasTheme ? 'rgba(22, 163, 74, 0.5) rgba(220, 38, 38, 0.2)' : 'rgba(147, 51, 234, 0.5) rgba(147, 51, 234, 0.2)'
                                    }}>
                                      {requests.slice(3).map((req, i) => (
                                        <motion.div 
                                          key={req.id} 
                                          initial={{opacity:0, x:-20}} 
                                          animate={{opacity:1, x:0}} 
                                          transition={{delay: i * 0.05}}
                                        >
                                          <div className={`flex justify-between items-center p-3 rounded-lg border ${
                                            isChristmasTheme
                                              ? 'bg-green-900/30 border-green-600/30'
                                              : 'bg-green-900/30 border-green-600/30'
                                          }`}>
                                            <div className="flex items-center space-x-3">
                                              <div className={`w-8 h-8 rounded-full flex items-center justify-center relative ${
                                                isChristmasTheme ? 'bg-green-600' : 'bg-green-600'
                                              }`}>
                                                <span className="text-white text-sm font-bold">{i + 4}</span>
                                                {req.skip_queue && (
                                                  <div className="absolute -top-1 -right-1 w-4 h-4 bg-yellow-500 rounded-full flex items-center justify-center">
                                                    <Star className="w-3 h-3 text-white" />
                                                  </div>
                                                )}
                                              </div>
                                              <div className="flex-1">
                                                <div className="flex items-center space-x-2">
                                                  <p className={`font-bold ${
                                                    isChristmasTheme ? 'text-green-300' : 'text-green-300'
                                                  }`}>
                                                    {capitalizeWords(req.song_title)}
                                                  </p>
                                                </div>
                                                <p className={`text-sm ${
                                                  isChristmasTheme ? 'text-green-400' : 'text-green-400'
                                                }`}>
                                                  by {capitalizeWords(req.artist_name)}
                                                </p>
                                              </div>
                                            </div>
                                            <div className="flex items-center space-x-2">
                                              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                            </div>
                                          </div>
                                        </motion.div>
                                      ))}
                                    </div>
                                    <button
                                      onClick={() => setShowQueueExpanded(false)}
                                      className={`text-center text-sm mt-3 w-full py-2 rounded-lg transition-colors ${
                                        isChristmasTheme
                                          ? 'text-green-300 hover:bg-green-900/30 hover:text-green-200'
                                          : 'text-purple-400 hover:bg-purple-900/30 hover:text-purple-300'
                                      }`}
                                    >
                                      Show Less
                                    </button>
                                  </div>
                                )}
                              </>
                            )}
                            </div>
                        ) : (
                          <div className="text-center py-4">
                            <div className="w-12 h-12 bg-purple-600/30 rounded-full flex items-center justify-center mx-auto mb-2">
                              <Music className="w-6 h-6 text-purple-400" />
                            </div>
                            <p className="text-purple-300 font-medium mb-1 text-sm">No requests yet</p>
                            <p className="text-purple-400 text-xs">Be the first to request a song!</p>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                      </motion.div>

                    {/* Request a Song Section */}
                    <Card className={`backdrop-blur-sm ${
                      isChristmasTheme
                        ? 'bg-red-900/20 border-red-500/50'
                        : 'bg-purple-800/20 border-purple-500/50'
                    }`}>
                      <CardHeader>
                        <CardDescription className={`text-center ${
                          isChristmasTheme ? 'text-green-300' : 'text-purple-300'
                        }`}>
                          {isChristmasTheme ? '🎄 Choose any song you\'d like to sing! 🎁' : 'Choose any song you\'d like to sing!'}
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="text-center">
                          <Button
                            onClick={() => {
                              setShowRequestModal(true);
                            }}
                            className={`text-white text-lg px-8 py-4 ${
                              isChristmasTheme
                                ? 'bg-gradient-to-r from-red-600 via-green-600 to-red-600 hover:from-red-700 hover:via-green-700 hover:to-red-700 shadow-lg shadow-red-500/50'
                                : 'bg-purple-600 hover:bg-purple-700'
                            }`}
                            size="lg"
                          >
                            {isChristmasTheme ? (
                              <>
                                <span className="text-xl mr-3">🎄</span>
                                Request a Song
                                <span className="text-xl ml-3">🎁</span>
                              </>
                            ) : (
                              <>
                                <Music className="w-5 h-5 mr-3" />
                                Request a Song
                              </>
                            )}
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  </>
                ) : (
                  /* Offline State - Rich Profile View */
                  <>
                    {/* Profile Header - Enhanced when offline */}
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6 }}
                      className="mb-8"
                    >
                      <div className="flex flex-col items-center text-center">
                        <div className="relative mb-4">
                          <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-purple-500/50 bg-purple-800 flex items-center justify-center shadow-xl">
                            {artist.profile_picture_url ? (
                              <img src={artist.profile_picture_url} alt={`${artist.karaoke_name || artist.artist_name} profile`} className="w-full h-full object-cover" />
                            ) : (
                              <Mic className="w-16 h-16 text-purple-300" />
                            )}
                          </div>
                        </div>
                        <h1 className="text-4xl font-bold text-white mb-2">{artist.karaoke_name || artist.artist_name}</h1>
                        {artist.tagline && (
                          <p className="text-purple-200 text-lg mb-4 italic max-w-md">{artist.tagline}</p>
                        )}
                        <div className="bg-purple-900/30 border border-purple-600/50 rounded-lg px-4 py-2 mb-4">
                          <span className="text-purple-300">● Currently Offline</span>
                        </div>
                        
                        {/* Social Media Links */}
                        {(artist.social_facebook || artist.social_x || artist.social_instagram || artist.social_tiktok) && (
                          <div className="flex items-center justify-center space-x-4 mt-4">
                            {artist.social_facebook && (
                              <a 
                                href={`https://facebook.com/${artist.social_facebook}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-purple-300 hover:text-blue-400 transition-colors"
                                aria-label="Facebook"
                              >
                                <Facebook className="w-6 h-6" />
                              </a>
                            )}
                            {artist.social_x && (
                              <a 
                                href={`https://x.com/${artist.social_x}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-purple-300 hover:text-white transition-colors"
                                aria-label="X (Twitter)"
                              >
                                <Twitter className="w-6 h-6" />
                              </a>
                            )}
                            {artist.social_instagram && (
                              <a 
                                href={`https://instagram.com/${artist.social_instagram}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-purple-300 hover:text-pink-400 transition-colors"
                                aria-label="Instagram"
                              >
                                <Instagram className="w-6 h-6" />
                              </a>
                            )}
                            {artist.social_tiktok && (
                              <a 
                                href={`https://tiktok.com/@${artist.social_tiktok}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-purple-300 hover:text-cyan-300 transition-colors"
                                aria-label="TikTok"
                              >
                                <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a4.83 4.83 0 0 0-1-.1 4.85 4.85 0 0 0-4.83 4.83 4.83 4.83 0 0 0 8.47 3.27l.01-.01v-7.19a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.32-.27z"/>
                                </svg>
                              </a>
                            )}
                          </div>
                        )}
                      </div>
                    </motion.div>

                    {/* Bio Section */}
                    {artist.bio && (
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                        className="mb-8"
                      >
                        <Card className="bg-purple-800/20 border-purple-500/50 backdrop-blur-sm">
                          <CardHeader>
                            <CardTitle className="text-purple-200 flex items-center">
                              <User className="w-5 h-5 mr-2" />
                              About {artist.karaoke_name || artist.artist_name}
                            </CardTitle>
                          </CardHeader>
                          <CardContent>
                            <p className="text-purple-100 leading-relaxed whitespace-pre-wrap">
                              {artist.bio}
                            </p>
                          </CardContent>
                        </Card>
                      </motion.div>
                    )}

                    {/* Offline Message */}
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: 0.4 }}
                    >
                      <Card className="bg-purple-800/20 border-purple-500/50 backdrop-blur-sm">
                        <CardHeader>
                          <CardTitle className="text-purple-200 text-center">Karaoke Not Live</CardTitle>
                          <CardDescription className="text-purple-300 text-center">
                            {artist.karaoke_name || artist.artist_name} is not currently hosting karaoke.
                          </CardDescription>
                        </CardHeader>
                        <CardContent className="text-center">
                          <p className="text-purple-400 mb-4">Check back later or ask the host when they'll be live!</p>
                          <Button
                            onClick={handleOpenRequestModal}
                            className="bg-purple-600 hover:bg-purple-700 text-white"
                            disabled
                          >
                            <Music className="w-4 h-4 mr-2" />
                            Request a Song (Available when live)
                          </Button>
                        </CardContent>
                      </Card>
                    </motion.div>
                  </>
                )}
        </div>

        {/* Request Modal */}
        <Dialog open={showRequestModal} onOpenChange={setShowRequestModal}>
          <DialogContent 
            className="bg-purple-900 border-purple-600 text-white max-w-md mx-auto flex flex-col p-0 max-h-[90vh]"
            onOpenAutoFocus={(e) => e.preventDefault()}
            onInteractOutside={(e) => {
              // Prevent closing when interacting outside - users must use Cancel or X button
              e.preventDefault();
            }}
          >
            <DialogHeader className="flex-shrink-0 px-6 pt-6 pb-4">
              <DialogDescription className="text-purple-200 font-bold text-base">
                <span className="drop-shadow-[0_0_8px_rgba(168,85,247,0.8)] text-purple-100">
                  There are <span className="text-yellow-300 drop-shadow-[0_0_10px_rgba(251,191,36,0.9)]">{requests.length}</span> {requests.length === 1 ? 'song' : 'songs'} in the queue!
                </span>
              </DialogDescription>
            </DialogHeader>
            
            {/* Skip The Queue - Informational Banner at Top (only show when 3+ songs) */}
            {showSkipQueueCTA && (
              <div className="px-6 pb-4 flex-shrink-0">
                <div className="p-3 bg-gradient-to-r from-yellow-600/20 to-amber-600/20 rounded-lg border-2 border-yellow-500/50 shadow-lg">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <Star className="w-5 h-5 text-yellow-400" />
                      <h4 className="text-yellow-200 font-bold text-lg">Skip the Queue</h4>
                    </div>
                    <div className="flex flex-col items-end">
                      <span className="text-lg font-bold text-yellow-300">
                        {format(currentPrice)}
                      </span>
                    </div>
                  </div>
                  <p className="text-yellow-100 text-xs">
                    Fill in the form below, then choose "Skip The Queue" to jump to the front for {format(currentPrice)}.
                    {paidRequestSettings?.showWaitTimes && estimatedWaitLabel && (
                      <> Current wait: <span className="font-semibold">{estimatedWaitLabel}</span>.</>
                    )}
                  </p>
                </div>
              </div>
            )}
            
            <div className="flex-1 overflow-y-auto min-h-0 px-6">
            <form id="request-song-form" onSubmit={handleRequestSubmit} className="space-y-3 pb-4">
              {useCustomForm && customFormFields ? (
                // Render custom form fields
                customFormFields.map((field) => (
                  <div key={field.id}>
                    {/* Only show Label wrapper for non-checkbox fields */}
                    {field.type !== 'checkbox' && (
                      <Label htmlFor={field.id} className="text-purple-200 text-sm">
                        {field.label}
                        {field.required && <span className="text-red-400 ml-1">*</span>}
                      </Label>
                    )}
                    
                    {field.type === 'textarea' ? (
                      <Textarea
                        id={field.id}
                        value={requestForm[field.id] || ''}
                        onChange={(e) => setRequestForm(prev => ({ ...prev, [field.id]: e.target.value }))}
                        required={field.required}
                        className="bg-purple-800 border-purple-600 text-white text-sm"
                        placeholder={`Enter ${field.label.toLowerCase()}`}
                        rows={field.id === 'note' ? 2 : 3}
                      />
                    ) : field.type === 'checkbox' ? (
                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id={field.id}
                          checked={requestForm[field.id] || false}
                          onChange={(e) => setRequestForm(prev => ({ ...prev, [field.id]: e.target.checked }))}
                          className="rounded border-purple-600 bg-purple-800 text-purple-400 focus:ring-purple-500"
                        />
                        <Label htmlFor={field.id} className="text-purple-200 text-sm">
                          {field.label}
                          {field.required && <span className="text-red-400 ml-1">*</span>}
                        </Label>
                      </div>
                    ) : field.type === 'email' ? (
                      <Input
                        id={field.id}
                        type="email"
                        value={requestForm[field.id] || ''}
                        onChange={(e) => setRequestForm(prev => ({ ...prev, [field.id]: e.target.value }))}
                        required={field.required}
                        className="bg-purple-800 border-purple-600 text-white text-sm h-9"
                        placeholder={`Enter ${field.label.toLowerCase()}`}
                      />
                    ) : field.type === 'tel' ? (
                      <Input
                        id={field.id}
                        type="tel"
                        value={requestForm[field.id] || ''}
                        onChange={(e) => setRequestForm(prev => ({ ...prev, [field.id]: e.target.value }))}
                        required={field.required}
                        className="bg-purple-800 border-purple-600 text-white text-sm h-9"
                        placeholder={`Enter ${field.label.toLowerCase()}`}
                      />
                    ) : (
                      <Input
                        id={field.id}
                        value={requestForm[field.id] || ''}
                        onChange={(e) => setRequestForm(prev => ({ ...prev, [field.id]: e.target.value }))}
                        required={field.required}
                        className="bg-purple-800 border-purple-600 text-white text-sm h-9"
                        placeholder={`Enter ${field.label.toLowerCase()}`}
                      />
                    )}
                  </div>
                ))
              ) : (
                // Render default form fields
                <>
                  <div>
                    <Label htmlFor="song_title" className="text-purple-200 text-sm">Song Title *</Label>
                    <Input
                      id="song_title"
                      value={requestForm.song_title}
                      onChange={(e) => setRequestForm(prev => ({ ...prev, song_title: e.target.value }))}
                      required
                      className="bg-purple-800 border-purple-600 text-white text-sm h-9"
                      placeholder="Enter song title"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="artist_name" className="text-purple-200 text-sm">Original Artist *</Label>
                    <Input
                      id="artist_name"
                      value={requestForm.artist_name}
                      onChange={(e) => setRequestForm(prev => ({ ...prev, artist_name: e.target.value }))}
                      required
                      className="bg-purple-800 border-purple-600 text-white text-sm h-9"
                      placeholder="Enter original artist name"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="requester_name" className="text-purple-200 text-sm">Your Name *</Label>
                    <Input
                      id="requester_name"
                      value={requestForm.requester_name}
                      onChange={(e) => setRequestForm(prev => ({ ...prev, requester_name: e.target.value }))}
                      required
                      className="bg-purple-800 border-purple-600 text-white text-sm h-9"
                      placeholder="Enter your name"
                    />
                  </div>
                </>
              )}
            </form>
            </div>
            
            <DialogFooter className="flex-shrink-0 border-t border-purple-700 pt-4 pb-6 px-6 mt-auto bg-purple-900 flex-col space-y-3">
              <div className="flex gap-2 flex-1 w-full">
                {/* Check if required fields are filled */}
                {(() => {
                  let fieldsFilled = false;
                  if (useCustomForm && customFormFields) {
                    const missingFields = customFormFields
                      .filter(field => field.required)
                      .filter(field => {
                        const value = requestForm[field.id];
                        return !value || (typeof value === 'string' && value.trim() === '');
                      });
                    fieldsFilled = missingFields.length === 0;
                  } else {
                    fieldsFilled = !!(requestForm.song_title && requestForm.artist_name && requestForm.requester_name);
                  }
                  
                  if (!fieldsFilled) {
                    return (
                      <Button
                        type="button"
                        disabled
                        className="flex-1 bg-gray-600 text-gray-400 cursor-not-allowed"
                      >
                        Fill in required fields
                      </Button>
                    );
                  }
                  
                  return (
                    <>
                      <Button
                        type="submit"
                        form="request-song-form"
                        disabled={submitting}
                        onClick={(e) => {
                          // Ensure skip_queue is false for free request
                          setRequestForm(prev => ({ ...prev, skip_queue: false }));
                        }}
                        className="flex-1 bg-purple-600 hover:bg-purple-700 text-white py-3 px-4"
                      >
                        <Send className="w-4 h-4 mr-2" />
                        {submitting ? 'Sending...' : 'Send Free Request'}
                      </Button>
                      {showSkipQueueCTA && (
                        <Button
                          type="button"
                          disabled={submitting || requests.filter(song => song.skip_queue).length >= paidRequestSettings.maxPaidRequests}
                          onClick={() => {
                            // Check if max paid requests reached
                            if (requests.filter(song => song.skip_queue).length >= paidRequestSettings.maxPaidRequests) {
                              toast({
                                title: "Queue Full",
                                description: `Maximum ${paidRequestSettings.maxPaidRequests} paid requests reached. Please try again later.`,
                                variant: "destructive"
                              });
                              return;
                            }
                            setShowSkipQueueModal(true);
                          }}
                          className="flex-1 bg-yellow-500 hover:bg-yellow-400 text-purple-900 border-yellow-300 disabled:opacity-50 disabled:cursor-not-allowed shadow-[0_0_12px_rgba(255,255,0,0.35)] transition-all py-3 px-4"
                        >
                          {showStarLabel ? (
                            <>
                              <Star className="w-4 h-4 mr-2" />
                              Skip The Queue
                            </>
                          ) : (
                            <>
                              <ArrowLeft className="w-4 h-4 mr-2" />
                              {paidRequestSettings?.showWaitTimes ? (
                                <>est. wait: {estimatedWaitLabel || '—'}</>
                              ) : (
                                <>Submit</>
                              )}
                            </>
                          )}
                        </Button>
                      )}
                    </>
                  );
                })()}
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowRequestModal(false);
                  resetForm();
                }}
                className={`w-full ${
                  isChristmasTheme
                    ? 'border-red-600 text-green-300 hover:bg-red-600/20'
                    : 'border-purple-600 text-purple-300 hover:bg-purple-600/20'
                }`}
              >
                Cancel
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Skip Queue Modal */}
        <Dialog open={showSkipQueueModal} onOpenChange={setShowSkipQueueModal}>
          <DialogContent className={`text-white max-w-md mx-auto ${
            isChristmasTheme
              ? 'bg-gradient-to-br from-red-950 via-green-950 to-red-900 border-red-600'
              : 'bg-purple-900 border-purple-600'
          }`}>
            <DialogHeader>
              <DialogTitle className="flex items-center">
                <Star className="w-5 h-5 mr-2" />
                Skip the Queue
              </DialogTitle>
              <DialogDescription>
                Jump to the front of the queue for {format(currentPrice)}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              {/* Current Queue Status */}
              <div className={`p-4 rounded-lg border ${
                isChristmasTheme
                  ? 'bg-red-900/30 border-red-600/50'
                  : 'bg-purple-800/30 border-purple-600/50'
              }`}>
                <h4 className={`font-semibold mb-2 ${
                  isChristmasTheme ? 'text-green-200' : 'text-purple-200'
                }`}>Current Queue Status</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className={isChristmasTheme ? 'text-green-300' : 'text-purple-300'}>Total songs in queue:</span>
                    <span className={`font-semibold ${
                      isChristmasTheme ? 'text-red-200' : 'text-purple-200'
                    }`}>{requests.length}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className={isChristmasTheme ? 'text-green-300' : 'text-purple-300'}>Paid requests ahead:</span>
                    <span className={`font-semibold ${
                      isChristmasTheme ? 'text-red-200' : 'text-purple-200'
                    }`}>
                      {requests.filter(song => song.skip_queue).length}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className={isChristmasTheme ? 'text-green-300' : 'text-purple-300'}>Your position if paid:</span>
                    <span className={`font-semibold ${
                      isChristmasTheme ? 'text-red-200' : 'text-purple-200'
                    }`}>
                      {requests.filter(song => song.skip_queue).length + 1}
                    </span>
                  </div>
                  <div className="pt-2 mt-2 border-t border-purple-600/30">
                    <div className="flex justify-between items-center">
                      <span className="text-purple-300">Skip Queue Price:</span>
                      <span className="font-semibold text-purple-200">
                        {format(currentPrice)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* How it works */}
              <div className="p-4 bg-purple-800/20 rounded-lg border border-purple-600/30">
                <h4 className="text-purple-200 font-semibold mb-2">How it works</h4>
                <ul className="text-purple-300 text-sm space-y-1">
                  <li>• Your song will be placed at the front of the queue</li>
                  <li>• You'll be behind any other paid requests</li>
                  <li>• The fee is {format(currentPrice)}</li>
                  <li>• Maximum {paidRequestSettings.maxPaidRequests} paid requests allowed</li>
                </ul>
              </div>

              {/* Current paid requests limit */}
              {requests.filter(song => song.skip_queue).length >= paidRequestSettings.maxPaidRequests && (
                <div className="p-4 bg-red-900/30 rounded-lg border border-red-600/50">
                  <div className="flex items-center space-x-2">
                    <X className="w-5 h-5 text-red-400" />
                    <span className="text-red-200 font-semibold">Queue Skipping Unavailable Right Now</span>
                  </div>
                  <p className="text-red-300 text-sm mt-1">
                    The number of current paid requests ({requests.filter(song => song.skip_queue).length}) has been reached. Please try again in a few moments when availability for paid requests becomes available once more.
                  </p>
                </div>
              )}
            </div>
            
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowSkipQueueModal(false)}
                className="border-purple-600 text-purple-300 hover:bg-purple-600/20"
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={() => {
                  // Check if max paid requests reached
                  if (requests.filter(song => song.skip_queue).length >= paidRequestSettings.maxPaidRequests) {
                    toast({
                      title: "Queue Full",
                      description: `Maximum ${paidRequestSettings.maxPaidRequests} paid requests reached. Please try again later.`,
                      variant: "destructive"
                    });
                    return;
                  }
                  
                  // Validate skip queue price is set
                  if (!paidRequestSettings.skipQueuePrice || paidRequestSettings.skipQueuePrice <= 0) {
                    toast({
                      title: "Payment Not Configured",
                      description: "The skip queue price hasn't been set yet. Please contact the karaoke host.",
                      variant: "destructive"
                    });
                    return;
                  }
                  
                  // Show payment modal (works with or without Stripe account - deferred payments supported)
                  setShowSkipQueueModal(false);
                  setShowPaymentModal(true);
                }}
                disabled={requests.filter(song => song.skip_queue).length >= paidRequestSettings.maxPaidRequests || !paidRequestSettings.skipQueuePrice || paidRequestSettings.skipQueuePrice <= 0}
                className={`text-white ${
                  isChristmasTheme
                    ? 'bg-gradient-to-r from-red-600 to-green-600 hover:from-red-700 hover:to-green-700'
                    : 'bg-purple-600 hover:bg-purple-700'
                }`}
              >
                <Star className="w-4 h-4 mr-2" />
                {isChristmasTheme ? '🎄 ' : ''}Skip Queue ({format(currentPrice)}){isChristmasTheme ? ' 🎁' : ''}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Payment Modal for Skip Queue */}
        <Dialog open={showPaymentModal} onOpenChange={setShowPaymentModal}>
          <DialogContent className={`text-white max-w-md mx-auto max-h-[90vh] overflow-y-auto p-0 ${
            isChristmasTheme
              ? 'bg-gradient-to-br from-red-950 via-green-950 to-red-900 border-red-600'
              : 'bg-purple-900 border-purple-600'
          }`}>
            <DialogHeader className="px-4 pt-4 pb-2">
              <DialogTitle className="flex items-center text-lg">
                <Star className="w-5 h-5 mr-2 text-yellow-400" />
                {usePayPal
                  ? 'Skip the queue with PayPal'
                  : 'Pay to Skip Queue'}
              </DialogTitle>
              <DialogDescription className="text-sm text-purple-200">
                {usePayPal
                  ? `Pay in ${artistCurrency} through PayPal to jump to the front of the queue.`
                  : 'Complete payment to skip to the front of the queue'}
              </DialogDescription>
            </DialogHeader>
            
            <div className="px-4 pb-4">
              {usePayPal ? (
                <Suspense
                  fallback={
                    <div className="flex items-center justify-center gap-2 py-8 text-purple-200">
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Loading checkout…
                    </div>
                  }
                >
                  <PayPalUSProcessor
                    artistCountry={artist?.country}
                    artistId={artist?.uid}
                    amount={currentPrice || 0}
                    description={`Skip queue payment for ${artist?.karaoke_name || 'Karaoke Host'}`}
                    onSuccess={handlePaymentSuccess}
                    onCancel={handlePaymentCancel}
                    mode="karaoke"
                    requestData={{
                      songTitle: requestForm.song_title || '',
                      originalArtist: requestForm.artist_name || '',
                      requesterName: requestForm.requester_name || 'Anonymous',
                      note: requestForm.note || '',
                      sessionId: paymentModalSessionId || liveStatus?.session_id || null,
                    }}
                  />
                </Suspense>
              ) : (
                <A2BPaymentForm
                  amount={currentPrice || 0}
                  artistId={artist?.uid}
                  description={`Skip queue payment for ${artist?.karaoke_name || 'Karaoke Host'}`}
                  onSuccess={handlePaymentSuccess}
                  onCancel={handlePaymentCancel}
                  mode="karaoke"
                  currency={artistCurrency}
                  requestData={{
                    songTitle: requestForm.song_title || '',
                    originalArtist: requestForm.artist_name || '',
                    requesterName: requestForm.requester_name || 'Anonymous',
                    note: requestForm.note || '',
                    sessionId: paymentModalSessionId || liveStatus?.session_id || null,
                  }}
                />
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
};



// SocialIcon component
const SocialIcon = ({ platform, handle }) => {
  const platformIcons = {
    facebook: <ExternalLink className="w-4 h-4" />,
    x: <ExternalLink className="w-4 h-4" />,
    instagram: <ExternalLink className="w-4 h-4" />,
    tiktok: <ExternalLink className="w-4 h-4" />
  };

  const platformUrls = {
    facebook: `https://facebook.com/${handle}`,
    x: `https://x.com/${handle}`,
    instagram: `https://instagram.com/${handle}`,
    tiktok: `https://tiktok.com/@${handle}`
  };

  return (
    <a
      href={platformUrls[platform]}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center space-x-2 text-purple-400 hover:text-purple-300 transition-colors"
    >
      <span>@{handle}</span>
      {platformIcons[platform]}
    </a>
  );
};

export default KaraokePublicPage;




